"use client";

import { CoverAiSticker } from "@/components/media/tilt-card";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import {
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, Volume1,
  Repeat, Repeat1, Shuffle, ListMusic, X, Heart, Music2, Radio, Loader2,
  Disc, Video, ArrowUpDown, LayoutList, LayoutGrid, Trash2,
  Pencil, Save, Check, ChevronDown, ChevronUp, Languages, Sparkles, AlertCircle,
  MessageCircle, Send, UserPlus, FileText, Globe, Users, Eye, EyeOff, Maximize2, Minimize2,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { cn, cleanArtworkUrl, resolveTrackArtwork } from "@/lib/utils";
import { canElementFullscreen, enterElementFullscreen, exitElementFullscreen, fullscreenElement, videoOrientation } from "@/lib/video-fullscreen";
import { useSidebar } from "@/components/ui/sidebar";
import { useCloseMenuWhenOpen } from "@/hooks/use-close-menu-when-open";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Link from "next/link";
import { Avatar, AvatarFallback, AvatarImage, getInitials } from "../ui/avatar";
import { useMediaPlayer, type MediaTrack } from "@/contexts/media-player-context";
import { useAuth } from "@/contexts/auth-context";
import { useRouter } from "next/navigation";
import { toggleFavorite, isFavorited as checkIsFavorited } from "@/services/favorites";
import ClaimCreditsButton from "@/components/claim-credits-button";
import { getEmbedUrl } from "@/lib/youtube";
import { AddToPlaylist } from "@/components/media/add-to-playlist";
import { TrackWaveform } from "@/components/media/track-waveform";
import { useOptionalUserLayoutPrefs } from "@/contexts/user-layout-prefs-context";

/** Bar waveform height per player size (compact 50px, medium 64px, tall 80px bar). */
const WAVE_HEIGHT = { compact: 22, medium: 32, tall: 44 } as const;
import { InlineAudioMeters } from "@/components/media/global-vu-strip";
import { DownloadButton } from "@/components/media/download-button";

/** A YouTube (or other linked) video has no file to sell or hand over, so the
 *  player shows no download button for it (Gordon 2026-11-01). A real video
 *  file (.mp4 etc.) keeps it, pay what you feel like any other file. */
function isLinkedVideo(tr: { youtubeId?: string; mediaType?: string; videoUrl?: string } | null | undefined): boolean {
  if (!tr) return false;
  if (tr.youtubeId || tr.mediaType === "youtube") return true;
  return !!tr.videoUrl && !/\.(mp4|m4v|webm|mov)(\?|#|$)/i.test(tr.videoUrl);
}
import { MarqueeText } from "@/components/ui/marquee-text";
import WebResultsSection from "@/components/web-results-section";
import { HelpBubble } from "@/components/help/help-bubble";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, limit as fbLimit, getDoc, doc as fbDoc, orderBy } from "firebase/firestore";
import { useAdmin } from "@/hooks/use-admin";
import { useToast } from "@/hooks/use-toast";
import { updateWork, deleteWork } from "@/services/media";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ArtistInput } from "@/components/profile/artist-input";
import { type TrackComment, subscribeTrackComments, addTrackComment, deleteTrackComment } from "@/services/track-comments";
import { ShareButton } from "@/components/ui/share-button";
import { SharePanel } from "@/components/ui/share-panel";
import { AiAuthorshipBadge } from "@/components/ui/ai-authorship-badge";
import { ExplicitBadge } from "@/components/ui/explicit-badge";
import { ParentalAdvisoryLabel } from "@/components/ui/parental-advisory-label";
import { SlideshowImage } from "@/components/ui/slideshow-image";
import { AudioReactiveArtwork, type ArtworkFxType } from "@/components/visual/audio-reactive-artwork";
import { ArtworkFxControl, resolveFx, useArtworkFx } from "@/components/visual/artwork-fx-control";
import { fetchWithAuth } from "@/lib/fetch-with-auth";
import { decodeAudioFromUrl, analyzeAudioBuffer } from "@/lib/audio-analysis";
import { useTranslations } from 'next-intl';
import { useHoldDragSwap } from '@/hooks/use-hold-drag-swap';
import { creditHref } from "@/components/credits/credit-link";

// One outline for every button in the player window's track header — claim
// credits, favorite, playlist, download, share (Gordon 2026-11-03: "outlines
// on all buttons please, not only the 2 last ones"). Width comes from the
// global button-outline rule (--btn-outline-border-width).
const SHEET_ACTION_BTN = "h-9 w-9 rounded-[var(--btn-radius)] border border-foreground bg-transparent text-foreground hover:bg-muted/40 hover:text-foreground";

type QueueSort = "original" | "az" | "za" | "artist";
type QueueView = "list" | "compact";

export function MediaPlayer() {
  // Hold and drag the player toggle across the screen to swap sides.
  const swap = useHoldDragSwap();
  const tp = useTranslations('player');
  const common = useTranslations('common');

  const {
    currentTrack, queue, queueIndex, queueContext, isPlaying, isLoading, audioError,
    currentTime, duration, volume, isMuted, shuffle, repeat,
    isActive, isExpanded, streamQuality, setStreamQuality,
    togglePlay, seek, setVolume, toggleMute,
    skipNext, skipPrev, toggleShuffle, cycleRepeat,
    setActive, setExpanded, formatTime, playTracks, removeFromQueue,
    setAutoNextProvider, analyserL,
  } = useMediaPlayer();
  const { toast } = useToast();

  // ── Audio-reactive thumbnail pulse ──────────────────────────────────────
  // The player-bar thumbnail zooms subtly with the music so it reads as a
  // live, tappable element (it toggles the expanded player). One RAF writes
  // a CSS var; every thumb reads it via transform — zero React re-renders.
  // When the analyser has no signal (iOS Web Audio quirks / YouTube), a
  // gentle breathing fallback keeps it moving during playback.
  useEffect(() => {
    const root = document.documentElement;
    // Decorative — skipped on touch devices: a 60fps style-var write is real
    // rendering load on phones, where the renderer is already the bottleneck
    // (iOS was killing the page under combined animation + scroll load).
    if (window.matchMedia?.('(pointer: coarse)').matches) {
      root.style.setProperty('--bb-thumb-pulse', '1');
      return;
    }
    if (!isPlaying) { root.style.setProperty('--bb-thumb-pulse', '1'); return; }
    let raf = 0;
    const buf = new Float32Array(1024);
    const tick = (t: number) => {
      let level = 0;
      const a = analyserL as AnalyserNode | null;
      if (a) {
        try {
          const n = Math.min(buf.length, a.fftSize);
          const view = buf.subarray(0, n);
          a.getFloatTimeDomainData(view);
          let s = 0;
          for (let i = 0; i < n; i++) s += view[i] * view[i];
          level = Math.min(1, Math.sqrt(s / n) * 4);
        } catch { level = 0; }
      }
      if (level <= 0.002) level = 0.3 + 0.25 * Math.sin(t / 300); // breathing fallback
      root.style.setProperty('--bb-thumb-pulse', (1 + level * 0.07).toFixed(4));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); root.style.setProperty('--bb-thumb-pulse', '1'); };
  }, [isPlaying, analyserL]);

  // Related tracks by same artist (loaded from Firestore when track changes)
  const [relatedByArtist, setRelatedByArtist] = useState<MediaTrack[]>([]);

  // Comments state
  const [trackComments, setTrackComments] = useState<TrackComment[]>([]);
  const [showTimelineComments, setShowTimelineComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [pinCommentToTime, setPinCommentToTime] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // Edit mode state
  const { isAdmin, isSuperAdmin } = useAdmin();
  const [workOwnerId, setWorkOwnerId] = useState<string | null>(null);
  // How-it-was-made provenance for the current track (human / hybrid / AI),
  // shown as a small badge in the player. Fetched from the work doc.
  const [trackAi, setTrackAi] = useState<{ level: string; percent?: number } | null>(null);
  // Listener's personal Artwork FX choice — remembered PER TRACK (overrides the
  // track's own default). Changing FX on one track never affects another.
  const [fxPref] = useArtworkFx(currentTrack?.id);
  // Explicit-content flag for the current track + one-time age gate.
  const [trackExplicit, setTrackExplicit] = useState(false);
  const [explicitGate, setExplicitGate] = useState(false);
  // Pause playback while the explicit age-gate is up.
  useEffect(() => { if (explicitGate && isPlaying) togglePlay(); }, [explicitGate]); // eslint-disable-line react-hooks/exhaustive-deps
  const [editFields, setEditFields] = useState({
    title: '', artist: '', genre: '', type: '', status: 'Published',
    explicit: false, allowDownloads: true, lyrics: '',
    coverArtUrl: '', downloadType: 'pwyw', downloadMinPrice: 100,
    hiResUrl: '',
  });
  // Resolved images: multi-image array for slideshow (release.images[] + artist.images[])
  // Tag resolved images with the trackId they belong to. React renders
  // BEFORE effects, so when currentTrack.id changes we render once with the
  // NEW currentTrack but the OLD resolvedImages — that's the "wrong
  // thumbnail flashing" Gordon kept hitting. Reading resolvedImages only
  // when its trackId matches currentTrack.id pins the artwork to the right
  // track from the very first paint.
  const [resolvedImages, setResolvedImages] = useState<{ trackId: string; urls: string[] }>({ trackId: '', urls: [] });
  // Resolved credits: enriched from work doc when currentTrack.credits is empty
  const [resolvedCredits, setResolvedCredits] = useState<{ role: string; name: string; href?: string; avatarUrl?: string }[]>([]);
  // Resolved featured artists: enriched from Firestore work doc
  const [resolvedFeaturedArtists, setResolvedFeaturedArtists] = useState<string[]>([]);
  // Resolved artist page link from the work/video doc's artistId — authoritative
  // over the name-slug fallback (which sent videos to the all-artists page).
  const [resolvedArtistLink, setResolvedArtistLink] = useState<string | null>(null);
  // The release this track belongs to, resolved from the artwork lookup below —
  // lets the album title link to its release even when the track object itself
  // carries no releaseId (Gordon: "release title is not linked in the player").
  const [resolvedReleaseId, setResolvedReleaseId] = useState<string>("");
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  // Credited contributors' user IDs for THIS track — anyone here may edit the
  // lyrics/transcript even if they don't own the work (Gordon: "contributors
  // can edit the text"). Populated during credit resolution below.
  const [contributorUids, setContributorUids] = useState<Set<string>>(new Set());
  // Inline lyrics/transcript editing state (Lyrics tab).
  const [lyricsEditing, setLyricsEditing] = useState(false);
  const [lyricsSaving, setLyricsSaving] = useState(false);
  const [lyricsSaved, setLyricsSaved] = useState(false);
  // Whether the current lyrics are an unverified AI transcription (auto) vs a
  // human-verified version — drives the "auto-transcribed, help correct" badge.
  const [lyricsMeta, setLyricsMeta] = useState<{ auto: boolean; verified: boolean }>({ auto: false, verified: false });
  // BPM + musical key — computed once (free, client-side Web Audio), cached on
  // the work doc, shown as a chip in the player. `pending` = analysing now.
  const [trackAnalysis, setTrackAnalysis] = useState<{ trackId: string; bpm: number; key: string; pending: boolean } | null>(null);
  // Track ids we've already attempted to analyse this session (prevents a retry
  // loop on tracks with no detectable tempo/key).
  const analyzedRef = useRef<Set<string>>(new Set());

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [metersExpanded, setMetersExpanded] = useState(false);
  // Waveform + meters can be folded away (Gordon 2026-11-02); remembered per device.
  const [metersShown, setMetersShown] = useState(true);
  useEffect(() => { try { if (localStorage.getItem('bb-player-meters') === 'hidden') setMetersShown(false); } catch { /* ignore */ } }, []);
  const toggleMeters = useCallback(() => setMetersShown(v => {
    try { localStorage.setItem('bb-player-meters', v ? 'hidden' : 'shown'); } catch { /* ignore */ }
    return !v;
  }), []);
  const [isFavoritedState, setIsFavoritedState] = useState(false);
  const [favDocId, setFavDocId] = useState<string | null>(null);
  const [hasMounted, setHasMounted] = useState(false);
  const [ytSettings, setYtSettings] = useState({
    autoplay: true, muted: false, loop: false, controls: true, modestBranding: true,
  });
  // Detected YouTube video aspect ratio (W/H). Falls back to 16/9 when we
  // can't reach the oEmbed endpoint. Gordon's report: "youtube embed height
  // is all wrong, need to be proportional with the width" — the fixed
  // aspect-video class was forcing 16:9 even for portrait / shorts content,
  // which made vertical videos shrink to a tiny letterboxed strip in the
  // middle of the iframe. We now probe the actual aspect and apply it to the
  // iframe wrapper so the embed grows to the video's real ratio.
  const [ytAspect, setYtAspect] = useState<number>(16 / 9);

  // Queue display state
  const [queueSort, setQueueSort] = useState<QueueSort>("original");
  const [queueView, setQueueView] = useState<QueueView>("list");
  // Video full screen (lib/video-fullscreen): the browser's own where it
  // exists — the app shell turns the screen on its side — else (iPhone) a
  // fixed frame turned 90° while the phone is upright.
  const videoFsRef = useRef<HTMLDivElement>(null);
  const [nativeFs, setNativeFs] = useState(false);
  const [pseudoFs, setPseudoFs] = useState<{ w: number; h: number } | null>(null);
  const videoFs = nativeFs || !!pseudoFs;
  // Closing the player (or switching to a track without video) ends the turned frame.
  useEffect(() => { if (!isSheetOpen) setPseudoFs(null); }, [isSheetOpen]);
  useEffect(() => {
    const onChange = () => {
      const on = !!videoFsRef.current && fullscreenElement() === videoFsRef.current;
      setNativeFs(on);
      if (on) setPseudoFs(null); // a late real full screen replaces the fallback frame
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);
  useEffect(() => {
    if (!pseudoFs) return;
    const size = () => setPseudoFs({ w: window.innerWidth, h: window.innerHeight });
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPseudoFs(null); };
    window.addEventListener("resize", size);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("resize", size); window.removeEventListener("keydown", onKey); };
  }, [!!pseudoFs]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleVideoFullscreen = useCallback(() => {
    const el = videoFsRef.current;
    if (!el) return;
    if (pseudoFs) { setPseudoFs(null); return; }
    if (fullscreenElement() === el) { void exitElementFullscreen().catch(() => {}); return; }
    const turnedFrame = () => { if (fullscreenElement() !== el) setPseudoFs({ w: window.innerWidth, h: window.innerHeight }); };
    if (canElementFullscreen(el)) {
      // Some in-app browsers (and webviews) neither enter full screen nor
      // refuse — fall back to the turned frame if nothing happened in 0.8 s.
      void enterElementFullscreen(el).catch(turnedFrame);
      window.setTimeout(turnedFrame, 800);
      return;
    }
    turnedFrame();
  }, [pseudoFs]);
  // Upright phone + landscape video → the frame is turned 90° to fill the
  // screen; a phone already on its side just fills it.
  const pseudoFsStyle: React.CSSProperties | null = pseudoFs
    ? (pseudoFs.h > pseudoFs.w && videoOrientation(ytAspect) === "landscape"
      ? { position: "fixed", top: "50%", left: "50%", width: pseudoFs.h, height: pseudoFs.w, transform: "translate(-50%, -50%) rotate(90deg)", zIndex: 300 }
      : { position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 300 })
    : null;
  // The playing row scrolls into view when the Queue opens and whenever the
  // playing track moves (Next, autoplay) — a callback ref runs on each attach.
  // Not while the window is opening: the queue has no scroller of its own, so
  // scrollIntoView moved the WHOLE window down to it (Gordon 2026-11-02: "opening
  // the media player scrolls the window down, it doesn't start at the top").
  const sheetOpenedAtRef = useRef(0);
  useEffect(() => { if (isSheetOpen) sheetOpenedAtRef.current = Date.now(); }, [isSheetOpen]);
  const currentQueueRowRef = useCallback((el: HTMLDivElement | null) => {
    if (!el || Date.now() - sheetOpenedAtRef.current < 1500) return;
    el.scrollIntoView({ block: "nearest" });
  }, []);
  // Super admin hide/erase, right from the queue row — with a confirm step
  // (Gordon 2026-09-30: "super admin can hide or erase track (xtra security
  // check) in track page for all tracks in listing").
  const [queueActionConfirm, setQueueActionConfirm] = useState<{ id: string; title: string; action: "hide" | "unhide" | "erase" } | null>(null);
  const [queueActionBusy, setQueueActionBusy] = useState(false);
  const [queueHiddenIds, setQueueHiddenIds] = useState<Set<string>>(new Set());


  const { isMobile, displayMode } = useSidebar();
  useCloseMenuWhenOpen(isSheetOpen);
  // The player window slides the record crate away (Gordon 2026-11-02).
  useEffect(() => { if (isSheetOpen) window.dispatchEvent(new CustomEvent('bb-crates', { detail: { open: false } })); }, [isSheetOpen]);
  const { user, userProfile } = useAuth();
  const router = useRouter();

  useEffect(() => {
    setHasMounted(true);
    try {
      const stored = localStorage.getItem('bb-youtube-settings');
      if (stored) setYtSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
    } catch {}
    const handleYtChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) setYtSettings(prev => ({ ...prev, ...detail }));
    };
    window.addEventListener('youtube-settings-changed', handleYtChange);
    return () => window.removeEventListener('youtube-settings-changed', handleYtChange);
  }, []);

  // A YouTube track's play/pause lives in the player state: relay it to the
  // iframe (the bar's pause did nothing for YouTube tracks before), and the
  // focus manager pauses it when other media takes over (lib/media-focus).
  useEffect(() => {
    const yt = (currentTrack as any)?.youtubeId || currentTrack?.mediaType === "youtube";
    if (!yt) return;
    const frame = document.querySelector("iframe[data-bb-player-yt]") as HTMLIFrameElement | null;
    try { frame?.contentWindow?.postMessage(JSON.stringify({ event: "command", func: isPlaying ? "playVideo" : "pauseVideo", args: "" }), "*"); } catch { /* ignore */ }
  }, [isPlaying, currentTrack]);

  // The YouTube iframe only exists inside the player window. Play on a
  // YouTube track with the window closed (a track restored after a refresh)
  // showed pause and sat at 0:00 (Gordon 2026-11-02) — open the window, whose
  // iframe autoplays. Closing the window unmounts the iframe, so the video
  // stops: mark it paused instead of leaving a phantom "playing".
  const ytPrevRef = useRef({ playing: isPlaying, open: isSheetOpen });
  useEffect(() => {
    const prev = ytPrevRef.current;
    ytPrevRef.current = { playing: isPlaying, open: isSheetOpen };
    const yt = (currentTrack as any)?.youtubeId || currentTrack?.mediaType === "youtube";
    if (!yt) return;
    if (isPlaying && !prev.playing && !isSheetOpen) setIsSheetOpen(true);
    else if (isPlaying && prev.open && !isSheetOpen) togglePlay();
  }, [isPlaying, isSheetOpen, currentTrack, togglePlay]);

  // Detect the YouTube video's actual aspect ratio so the embed wrapper grows
  // proportionally instead of forcing every video into a 16:9 letterbox.
  //
  // We probe the maxresdefault thumbnail — for landscape it's 1280×720
  // (16:9), for portrait Shorts it's 1080×1920 (9:16), and for older 4:3
  // uploads YouTube serves a padded but proportional crop. naturalWidth /
  // naturalHeight is a reliable proxy for the video's actual aspect ratio
  // without needing the IFrame Player API (which would mean waiting for the
  // player to load, then bouncing postMessage events through a useEffect).
  // Falls back to maxres → hq → 16:9 when the request fails.
  useEffect(() => {
    const ytId = (currentTrack as any)?.youtubeId as string | undefined;
    if (!ytId) { setYtAspect(16 / 9); return; }
    let cancelled = false;
    const tryUrl = (url: string, onFail: () => void) => {
      const img = new window.Image();
      img.onload = () => {
        if (cancelled) return;
        if (img.naturalWidth > 0 && img.naturalHeight > 0) {
          setYtAspect(img.naturalWidth / img.naturalHeight);
        } else {
          onFail();
        }
      };
      img.onerror = () => { if (!cancelled) onFail(); };
      img.src = url;
    };
    tryUrl(`https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`, () => {
      tryUrl(`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`, () => {
        if (!cancelled) setYtAspect(16 / 9);
      });
    });
    return () => { cancelled = true; };
  }, [(currentTrack as any)?.youtubeId]);

  // ── Fetch related tracks by same artist when the current track changes ────────
  useEffect(() => {
    if (!currentTrack?.id) { setRelatedByArtist([]); return; }
    let isMounted = true;
    const currentId = currentTrack.id;
    let artistId = (currentTrack as any).artistId as string | undefined;
    const artistSlug = currentTrack.artistSlug;
    const artistName = currentTrack.artist;
    const genre = currentTrack.genre;

    async function fetchRelated() {
      const tracks: MediaTrack[] = [];
      try {
        // artistId isn't part of the MediaTrack type (it's read via `as any`
        // below), so most of the app's many playTrack()/playTracks() call
        // sites never set it. Without it, the release-cover-map fetch AND the
        // artist-image fallback further down were BOTH skipped (both gated on
        // `if (artistId)`), leaving "More from X" thumbnails blank even when
        // the artist has real cover art — resolve a real id from the slug
        // (or name) first so that existing fallback chain actually runs.
        if (!artistId && (artistSlug || artistName)) {
          try {
            const artistQuery = artistSlug
              ? query(collection(db, 'artists'), where('slug', '==', artistSlug), fbLimit(1))
              : query(collection(db, 'artists'), where('name', '==', artistName), fbLimit(1));
            const artistLookup = await getDocs(artistQuery);
            if (!artistLookup.empty) artistId = artistLookup.docs[0].id;
          } catch { /* non-critical — falls through to name/genre matching below */ }
        }

        // Try by artistId first, then artist name, then genre
        const constraint = artistId
          ? where('artistId', '==', artistId)
          : artistName
            ? where('artist', '==', artistName)
            : genre
              ? where('genre', '==', genre)
              : null;

        if (!constraint) return;

        // Fetch works + releases for this artist in parallel
        const [snap, relSnap] = await Promise.all([
          getDocs(query(collection(db, 'works'), constraint, fbLimit(25))),
          artistId
            ? getDocs(query(collection(db, 'releases'), where('artistId', '==', artistId), fbLimit(20)))
            : Promise.resolve(null),
        ]);

        // Build release cover map: workId → coverArtUrl
        const coverMap = new Map<string, string>();
        let anyReleaseCover = '';
        if (relSnap) {
          relSnap.docs.forEach(rd => {
            const r = rd.data();
            const cover = r.coverArtUrl || (Array.isArray(r.images) && r.images[0]) || '';
            if (cover && !anyReleaseCover) anyReleaseCover = cover;
            if (cover && Array.isArray(r.tracks)) {
              r.tracks.forEach((t: any) => {
                const wid = t.workId || t.id;
                if (wid && !coverMap.has(wid)) coverMap.set(wid, cover);
              });
            }
          });
        }

        // Artist image as last-resort fallback
        let artistImg = '';
        if (artistId) {
          try {
            const artSnap = await getDoc(fbDoc(db, 'artists', artistId));
            if (artSnap.exists()) {
              const a = artSnap.data();
              artistImg = a.imageUrl || a.thumbnailUrl || (Array.isArray(a.images) && a.images[0]) || '';
            }
          } catch { /* non-critical */ }
        }

        snap.docs.forEach(d => {
          const data = d.data();
          if (d.id === currentId) return; // skip currently-playing track
          if (!data.audioUrl) return;

          // Resolve thumbnail — skip the app branding logo if accidentally saved as cover art
          const ownCover = data.coverArtUrl && !data.coverArtUrl.includes('/logos/bb-logo')
            ? data.coverArtUrl : '';
          const imageUrl = ownCover
            || coverMap.get(d.id)
            || anyReleaseCover
            || artistImg
            || data.imageUrl
            || '';

          tracks.push({
            id: d.id,
            title: data.title || 'Unknown',
            // Works store artist/album as objects as often as strings — an
            // object here crashed the page (React #31) once the grid rendered.
            artist: (typeof data.artist === 'string' ? data.artist : data.artist?.name) || data.artistName || '',
            album: (typeof data.album === 'string' ? data.album : data.album?.title) || '',
            imageUrl,
            audioUrl: data.audioUrl,
            genre: data.genre || '',
            mediaType: 'audio',
            artistSlug: data.artistSlug || '',
          });
        });
      } catch (err) {
        console.error('Error fetching related tracks:', err);
      }
      if (isMounted) setRelatedByArtist(tracks);
    }
    fetchRelated();
    return () => { isMounted = false; };
  }, [currentTrack?.id]);

  // ── Register same-genre auto-next provider ────────────────────────────────────
  useEffect(() => {
    if (!currentTrack?.id) {
      setAutoNextProvider(null);
      return;
    }
    const genre = currentTrack.genre;
    const currentId = currentTrack.id;

    setAutoNextProvider(async () => {
      if (!genre) return relatedByArtist;
      try {
        const q = query(collection(db, 'works'), where('genre', '==', genre), fbLimit(40));
        const snap = await getDocs(q);
        return snap.docs
          .filter(d => d.id !== currentId && d.data().audioUrl)
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              title: data.title || 'Unknown',
              artist: (typeof data.artist === 'string' ? data.artist : data.artist?.name) || data.artistName || '',
              album: (typeof data.album === 'string' ? data.album : data.album?.title) || '',
              imageUrl: data.coverArtUrl || data.imageUrl || '',
              audioUrl: data.audioUrl,
              genre: data.genre || '',
              mediaType: 'audio' as const,
              artistSlug: data.artistSlug || '',
            };
          });
      } catch {
        return relatedByArtist;
      }
    });

    return () => setAutoNextProvider(null);
  }, [currentTrack?.id, currentTrack?.genre, setAutoNextProvider]);

  // ── Load work doc for edit mode + credits when track changes ────────────────
  useEffect(() => {
    if (!currentTrack?.id) { setWorkOwnerId(null); setTrackAi(null); setTrackExplicit(false); setResolvedImages({ trackId: '', urls: [] }); setResolvedCredits([]); setResolvedFeaturedArtists([]); setResolvedArtistLink(null); setResolvedReleaseId(""); return; }
    // Optimistic AI level + explicit flag from the caller; the fetch below enriches.
    setTrackAi(currentTrack.aiLevel ? { level: currentTrack.aiLevel, percent: currentTrack.aiPercent } : null);
    setTrackExplicit(!!currentTrack.explicit);
    setResolvedArtistLink(null);
    setResolvedReleaseId("");
    // Capture synchronously so the async closure always has the right value even if
    // the context re-renders (e.g. branding onSnapshot) before the Firestore fetch completes.
    const capturedImageUrl = currentTrack.imageUrl || '';
    const capturedImages = (currentTrack as any).images as string[] | undefined;
    // Optimistic: use passed-in images array, or fall back to single imageUrl.
    // CRITICAL: setResolvedImages([]) FIRST so the UI never shows the previous
    // track's resolved image while we re-fetch for the new track. The
    // optimistic write below covers the common case where the caller passed
    // an imageUrl in — but when they didn't (e.g. queue navigation from a
    // place that doesn't enrich), clearing first prevents the stale-cover
    // flash Gordon kept seeing.
    const optimisticImages = capturedImages?.filter(Boolean).length
      ? capturedImages!.filter(Boolean)
      : capturedImageUrl ? [capturedImageUrl] : [];
    setResolvedImages({ trackId: currentTrack.id, urls: optimisticImages }); // optimistic — update when Firestore resolves
    setResolvedCredits(currentTrack.credits || []);
    // Optimistic featured artists from what the caller passed; Firestore will enrich below
    setResolvedFeaturedArtists(currentTrack.featuredArtists ?? []);
    let isMounted = true;
    const trackId = currentTrack.id;
    const capturedAlbum = currentTrack.album || '';
    const capturedCredits = currentTrack.credits || [];
    const capturedTitle = currentTrack.title || '';
    const capturedArtist = currentTrack.artist || '';
    const capturedGenre = currentTrack.genre || '';
    getDoc(fbDoc(db, 'works', trackId)).then(async snap => {
      if (!isMounted) return;
      let data: any = snap.data() || {};
      // Videos live in the `videos` collection, not `works`. When the works doc
      // is missing (a playing video), fall back to the video doc so credits +
      // the artist link resolve for videos too.
      if (!snap.exists()) {
        try {
          const vidSnap = await getDoc(fbDoc(db, 'videos', trackId));
          if (vidSnap.exists()) data = vidSnap.data() || {};
        } catch { /* keep empty */ }
        if (!isMounted) return;
      }
      setWorkOwnerId(data.owner?.userId || data.uploadedBy || null);

      // ── How it was made (AI provenance) ────────────────────────────────────
      // Prefer the canonical aiLevel/aiPercent; else map the "How was this made?"
      // disclosure (human / ai-assisted / ai-generated) onto the badge scale.
      {
        const discMap: Record<string, string> = { 'human': 'human', 'ai-assisted': 'hybrid', 'ai-generated': 'ai' };
        const lvl = (data.aiLevel as string) || (data.aiDisclosure?.level ? discMap[data.aiDisclosure.level] : '') || '';
        const pct = typeof data.aiPercent === 'number' ? data.aiPercent
                  : typeof data.aiDisclosure?.percentAI === 'number' ? data.aiDisclosure.percentAI : undefined;
        setTrackAi(lvl ? { level: lvl, percent: pct } : null);
      }
      // Explicit flag → drive the badge + one-time age gate.
      {
        const exp = !!(data.explicit || currentTrack.explicit);
        setTrackExplicit(exp);
        try {
          if (exp && typeof window !== 'undefined' && localStorage.getItem('bb-explicit-ok') !== '1') {
            setExplicitGate(true);
          }
        } catch { /* ignore */ }
      }

      // ── Resolve thumbnail (priority order) ─────────────────────────────────
      // 1. Release cover art   — album art is always preferred
      // 2. Work's own cover    — track-specific upload (singles / standalone)
      // 3. Creator profile img — last resort (artist photo)
      // 4. capturedImageUrl    — stability fallback, never show blank after optimistic paint
      //
      // Using captured* vars (not currentTrack.*) avoids stale-closure mismatches when
      // branding context re-renders between effect creation and async completion.

      // Tier 1 — find the release this track belongs to. Was using
      // `where('tracks', 'array-contains-any', [{ workId: trackId }, …])`
      // which is brittle — Firestore's array-contains on objects requires
      // an EXACT field match, but release.tracks entries also carry title /
      // audioUrl / etc., so the query frequently missed and we ended up
      // showing the wrong release's artwork in the footer. Prefer the
      // denormalized `workIds: string[]` field when present (written by the
      // bulk-upload save path); fall back to a small scan + client filter
      // for legacy docs. Capped at 25 most-recent releases so it's cheap.
      let releaseImg = '';
      let releaseImagesArr: string[] = [];
      try {
        let relData: any = null;
        let releaseDocId = ''; // captured so the album title can link to its release
        // Fastest + most authoritative — the work doc's own releaseId
        // pointer. Was missing from the chain, so any track on an old
        // release whose `workIds` hadn't been denormalised (the array-
        // contains query below) would fall through to the 50-release
        // scan and frequently grab the WRONG release's cover. That's
        // the "media player thumbnail is not showing the correct
        // thumbnail" Gordon flagged.
        const directReleaseId = data.releaseId || (currentTrack as any)?.releaseId || '';
        if (directReleaseId) {
          try {
            const directSnap = await getDoc(fbDoc(db, 'releases', directReleaseId));
            if (directSnap.exists()) { relData = directSnap.data(); releaseDocId = directReleaseId; }
          } catch { /* fall through to the lookup paths */ }
        }
        // Fast path — `workIds` is the denormalised array of work IDs that
        // belong to the release. Indexed via array-contains.
        if (!relData) {
          const relByWorkIds = await getDocs(query(
            collection(db, 'releases'),
            where('workIds', 'array-contains', trackId),
            fbLimit(1),
          ));
          if (!relByWorkIds.empty) {
            relData = relByWorkIds.docs[0].data();
            releaseDocId = relByWorkIds.docs[0].id;
          }
        }
        if (!relData) {
          // Slow path — scan recent releases, look for the workId in each
          // doc's tracks[].workId. Limited so it never devolves into a
          // catalog scan; for older releases admins can add the workIds
          // field via Heal Tracks. Bug Gordon hit: a compilation /
          // re-release that references the same workId can win this
          // race, producing the WRONG cover on the player. Prefer
          // releases that ALSO match the captured artist string before
          // settling for any release that just contains the workId.
          const scanSnap = await getDocs(query(
            collection(db, 'releases'),
            orderBy('createdAt', 'desc'),
            fbLimit(50),
          ));
          const containsWork = (d: any) => {
            const tracks = Array.isArray(d.tracks) ? d.tracks : [];
            return tracks.some((t: any) => t?.workId === trackId || t?.id === trackId);
          };
          const norm = (s: string) => (s || '').toLowerCase().trim();
          const wantArtist = norm(data.artist?.name || data.artistName || capturedArtist);
          let artistMatched: any = null; let artistMatchedId = '';
          let anyMatched: any = null;    let anyMatchedId = '';
          for (const doc of scanSnap.docs) {
            const d = doc.data();
            if (!containsWork(d)) continue;
            const relArtist = norm(d.artist?.name || d.artistName);
            if (wantArtist && relArtist && relArtist === wantArtist) {
              artistMatched = d; artistMatchedId = doc.id;
              break;
            }
            if (!anyMatched) { anyMatched = d; anyMatchedId = doc.id; }
          }
          relData = artistMatched || anyMatched || null;
          releaseDocId = artistMatched ? artistMatchedId : (anyMatched ? anyMatchedId : releaseDocId);
        }
        if (relData) {
          releaseImg = relData.coverArtUrl || '';
          releaseImagesArr = Array.isArray(relData.images) && relData.images.length > 0
            ? relData.images.filter(Boolean)
            : releaseImg ? [releaseImg] : [];
        }
        // Fallback: match release by album title stored on the work doc
        if (!releaseImg && (data.album || capturedAlbum)) {
          const titleQ = query(
            collection(db, 'releases'),
            where('title', '==', data.album || capturedAlbum),
            fbLimit(3),
          );
          const titleSnap = await getDocs(titleQ);
          titleSnap.docs.forEach(d => {
            if (!releaseImg && d.data().coverArtUrl) {
              releaseImg = d.data().coverArtUrl;
              const rd = d.data();
              releaseImagesArr = Array.isArray(rd.images) && rd.images.length > 0
                ? rd.images.filter(Boolean)
                : [releaseImg];
              if (!releaseDocId) releaseDocId = d.id;
            }
          });
        }
        // Publish the resolved release id so the album title can link to it.
        if (isMounted && releaseDocId) setResolvedReleaseId(releaseDocId);
      } catch {}

      // Tier 2 — work doc's own uploaded cover art (for standalone singles / uploads)
      const workImg = data.coverArtUrl || data.imageUrl || '';

      // Tier 3 — artist images (multi-image slideshow) + creator profile fallback
      let artistImagesArr: string[] = [];
      // Videos store the artist nested (artist.docId / artist.id); works use
      // top-level artistId. Cover both so the link resolves either way.
      const artistId = data.artistId || data.artist?.docId || data.artist?.id || (currentTrack as any)?.artistId || '';
      if (artistId) {
        try {
          const artSnap = await getDoc(fbDoc(db, 'artists', artistId));
          if (artSnap.exists()) {
            const aData = artSnap.data();
            if (isMounted) setResolvedArtistLink(`/artists/${aData.slug || artistId}`);
            if (Array.isArray(aData.images) && aData.images.length > 0) {
              artistImagesArr = aData.images.filter(Boolean);
            } else {
              const aImg = aData.imageUrl || aData.thumbnailUrl || '';
              if (aImg) artistImagesArr = [aImg];
            }
          }
        } catch {}
      }
      // Creator profile image (last resort when no release, no work cover, no artist)
      let creatorImg = '';
      if (!releaseImg && !workImg && artistImagesArr.length === 0) {
        const ownerId = data.owner?.userId || data.uploadedBy || data.createdByUid || '';
        if (ownerId) {
          try {
            const userSnap = await getDoc(fbDoc(db, 'users', ownerId));
            if (userSnap.exists()) {
              const ud = userSnap.data() as { profileImageUrl?: string; imageUrl?: string };
              creatorImg = ud.profileImageUrl || ud.imageUrl || '';
            }
          } catch {}
        }
      }

      // Build the final deduplicated images array.
      //
      // Order rule Gordon needs: the TRACK's own cover wins when set,
      // because that's the only authoritative cover for singles, beats,
      // and DJ mixes uploaded with custom artwork. Release art only
      // wins when the track has no cover of its own. Previously the
      // chain was release → work → artist, which forced every track on
      // a compilation to share the comp's cover even when the original
      // single had its own art — and any wrong-release pick from the
      // scan path above immediately overrode the correct track cover.
      const seen = new Set<string>();
      const finalImages: string[] = [];
      const candidates = workImg ? [
        workImg,
        ...releaseImagesArr,
        ...artistImagesArr,
        creatorImg,
        capturedImageUrl,
      ] : [
        ...releaseImagesArr,
        workImg,
        ...artistImagesArr,
        creatorImg,
        capturedImageUrl,
      ];
      for (const img of candidates) {
        const cleaned = cleanArtworkUrl(img);
        if (cleaned && !seen.has(cleaned)) { seen.add(cleaned); finalImages.push(cleaned); }
      }

      // Apply: never regress below the optimistic image already on screen
      if (isMounted) setResolvedImages({ trackId, urls: finalImages.length > 0 ? finalImages : optimisticImages });

      // ── Resolve featured artists from work doc ───────────────────────────
      if (isMounted && Array.isArray(data.featuredArtists) && data.featuredArtists.length > 0) {
        setResolvedFeaturedArtists(data.featuredArtists as string[]);
      } else if (isMounted) {
        setResolvedFeaturedArtists([]);
      }

      // ── Resolve credits from work doc ────────────────────────────────────
      const rawCredits: any[] = data.contributors || data.credits || capturedCredits;
      if (rawCredits.length > 0) {
        // Collect credited user IDs so contributors can edit the lyrics. Grab
        // any id stored directly on the credit, plus (below) any resolved by
        // matching the credited name to a user profile. Also honour the work's
        // owner id.
        const uids = new Set<string>();
        rawCredits.forEach((c: any) => {
          const id = c.userId || c.uid || c.id;
          if (typeof id === 'string' && id) uids.add(id);
        });
        if (typeof data.owner?.userId === 'string') uids.add(data.owner.userId);
        // Normalise: each entry has { name, role/roles }
        const credits = rawCredits.map((c: any) => {
          const name = c.name || c.displayName || 'Unknown';
          const roles: string[] = Array.isArray(c.roles) ? c.roles : c.role ? [c.role] : ['Contributor'];
          const uid = typeof (c.userId || c.uid) === 'string' ? String(c.userId || c.uid) : '';
          return { name, role: roles.join(', '), href: '', avatarUrl: '', uid };
        });
        // Look up user profiles for linking: query userProfiles where displayName matches
        try {
          const uniqueNames = [...new Set(credits.map(c => c.name))];
          // Batch lookup: Firestore doesn't support 'in' on displayName, so query by each name
          // Limit to first 10 to avoid excessive reads
          const profileLookups = uniqueNames.slice(0, 10).map(n =>
            getDocs(query(collection(db, 'userProfiles'), where('displayName', '==', n), fbLimit(1)))
          );
          const profileResults = await Promise.all(profileLookups);
          const nameToProfile = new Map<string, { uid: string; photoURL: string }>();
          profileResults.forEach(snap => {
            if (!snap.empty) {
              const d = snap.docs[0];
              const profile = d.data();
              const dName = profile.displayName || '';
              nameToProfile.set(dName, { uid: d.id, photoURL: profile.photoURL || profile.imageUrl || '' });
            }
          });
          // Also check artists collection for linking to artist pages
          const artistLookups = uniqueNames.slice(0, 10).map(n =>
            getDocs(query(collection(db, 'artists'), where('name', '==', n), fbLimit(1)))
          );
          const artistResults = await Promise.all(artistLookups);
          const nameToArtist = new Map<string, string>(); // name → artist slug/id
          artistResults.forEach(snap => {
            if (!snap.empty) {
              const d = snap.docs[0];
              const a = d.data();
              nameToArtist.set(a.name, a.slug || d.id);
            }
          });
          // Members by name too (the users collection — userProfiles misses most).
          const missing = uniqueNames.filter(n => !nameToArtist.has(n) && !nameToProfile.has(n)
            && !credits.some(c => c.name === n && c.uid)).slice(0, 10);
          const userResults = await Promise.all(missing.map(n =>
            getDocs(query(collection(db, 'users'), where('displayName', '==', n), fbLimit(1))).catch(() => null)));
          userResults.forEach(snap => {
            const d = snap?.docs[0];
            if (!d) return;
            const u = d.data() as any;
            nameToProfile.set(u.displayName || '', { uid: d.id, photoURL: u.profileImageUrl || u.photoURL || '' });
          });
          // Every name a link (Gordon 2026-11-02: "make sure you can click on
          // credits names"): its artist page, else the account the credit is
          // linked to (claimable placeholders included), else a member by name.
          credits.forEach(c => {
            const artist = nameToArtist.get(c.name);
            const profile = nameToProfile.get(c.name);
            if (artist) {
              c.href = `/artists/${artist}`;
            } else if (c.uid) {
              c.href = `/profile/${c.uid}`;
              if (profile?.uid === c.uid) c.avatarUrl = profile.photoURL;
            } else if (profile) {
              c.href = `/profile/${profile.uid}`;
              c.avatarUrl = profile.photoURL;
              uids.add(profile.uid); // name-matched contributor → can edit lyrics
            }
          });
        } catch { /* non-fatal: credits still show without links */ }
        if (isMounted) { setResolvedCredits(credits); setContributorUids(uids); }
      } else if (isMounted) {
        setContributorUids(new Set());
      }

      // ── Seed BPM / key from the stored analysis (computed once, cached) ────
      if (isMounted) {
        setTrackAnalysis({
          trackId,
          bpm: Number(data.bpm) || 0,
          key: typeof data.musicalKey === 'string' ? data.musicalKey : '',
          pending: false,
        });
        // Lyrics verification state: an auto transcription stays "unverified"
        // until a human edits it. Default verified when lyrics exist but no
        // flags were ever set (legacy human-entered lyrics).
        setLyricsMeta({
          auto: data.lyricsAuto === true,
          verified: data.lyricsVerified === true || (!!data.lyrics && data.lyricsAuto !== true),
        });
      }

      setEditFields({
        title: data.title || capturedTitle,
        artist: (typeof data.artist === 'string' ? data.artist : data.artist?.name) || data.artistName || capturedArtist,
        genre: data.genre || capturedGenre,
        type: data.type || '',
        status: data.status || 'Published',
        explicit: data.explicit || false,
        allowDownloads: data.allowDownloads ?? true,
        lyrics: data.lyrics || '',
        coverArtUrl: data.coverArtUrl || data.imageUrl || '',
        downloadType: data.downloadType || 'pwyw',
        downloadMinPrice: data.downloadMinPrice || 0,
        hiResUrl: data.hiResUrl || '',
      });
    }).catch(() => {
      // Firestore fetch failed — restore the optimistic images so the thumbnail never disappears
      if (isMounted) setResolvedImages({ trackId, urls: optimisticImages });
    });
    return () => { isMounted = false; };
  }, [currentTrack?.id]);

  // Auto-reset queue sort to original when the album changes
  useEffect(() => {
    setQueueSort("original");
  }, [currentTrack?.album]);

  useEffect(() => {
    if (!user?.uid || !currentTrack?.id) {
      setIsFavoritedState(false);
      setFavDocId(null);
      return;
    }
    checkIsFavorited(user.uid, currentTrack.id).then((docId) => {
      setIsFavoritedState(!!docId);
      setFavDocId(docId);
    });
  }, [user?.uid, currentTrack?.id]);

  // ── Subscribe to track comments ────────────────────────────────────────────
  useEffect(() => {
    if (!currentTrack?.id) { setTrackComments([]); return; }
    // Try to get the workId from the track (strip release prefix if needed)
    const trackId = currentTrack.id.includes('-track-') ? (currentTrack as any).workId || currentTrack.id : currentTrack.id;
    const unsub = subscribeTrackComments(trackId, setTrackComments);
    return () => unsub();
  }, [currentTrack?.id]);

  const handleToggleFavorite = useCallback(async () => {
    if (!currentTrack) return;
    if (!user?.uid) { router.push('/signin'); return; }
    const isYT = !!(currentTrack.youtubeId || currentTrack.mediaType === "youtube");
    try {
      const result = await toggleFavorite(user.uid, {
        itemId: currentTrack.id,
        itemType: isYT ? "video" : currentTrack.mediaType === "radio" ? "radio" : "track",
        title: currentTrack.title,
        subtitle: currentTrack.artist || "",
        imageUrl: currentTrack.imageUrl || "",
        audioUrl: !isYT ? (currentTrack.audioUrl || "") : "",
        youtubeId: isYT ? (currentTrack.youtubeId || "") : "",
        link: currentTrack.artistSlug ? `/artists/${currentTrack.artistSlug}` : "",
      });
      setIsFavoritedState(result.added);
      setFavDocId(result.favoriteId || null);
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  }, [user?.uid, currentTrack, router]);

  // Can the current user edit this track's METADATA (cover, fields)? — owner/admin.
  const canEdit = isSuperAdmin || isAdmin || (!!workOwnerId && workOwnerId === user?.uid);
  // Lyrics generate/transcribe is available to any admin OR creator (matches the
  // /music/[id] lyrics panel, which lets creators transcribe) — so the button
  // isn't missing in the player for creators (Gordon: "missing auto generate button").
  const profileIsEditor = !!userProfile && (
    (userProfile as any).accountType === 'creator' ||
    (userProfile as any).accountType === 'admin' ||
    (userProfile as any).accountType === 'superadmin' ||
    ((userProfile as any).roles || []).includes('admin') ||
    ((userProfile as any).roles || []).includes('superadmin')
  );
  // A credited contributor on THIS track (matched by uid) may edit the lyrics
  // even without owner/admin rights (Gordon: "contributors can edit the text").
  const userIsContributor = !!user?.uid && contributorUids.has(user.uid);
  const canEditLyrics = canEdit || profileIsEditor || userIsContributor;

  const handleSaveEdit = useCallback(async () => {
    if (!currentTrack?.id) return;
    setEditSaving(true);
    try {
      await updateWork(currentTrack.id, {
        title: editFields.title.trim(),
        artist: editFields.artist.trim(),
        genre: editFields.genre.trim(),
        type: editFields.type,
        status: editFields.status,
        explicit: editFields.explicit,
        allowDownloads: editFields.allowDownloads,
        lyrics: editFields.lyrics,
        coverArtUrl: editFields.coverArtUrl.trim(),
        downloadType: editFields.downloadType as 'free' | 'pwyw' | 'paid',
        downloadMinPrice: editFields.downloadMinPrice,
        hiResUrl: editFields.hiResUrl.trim(),
      });
      if (editFields.coverArtUrl.trim() && currentTrack?.id) setResolvedImages({ trackId: currentTrack.id, urls: [editFields.coverArtUrl.trim()] });
      setEditSaved(true);
      setTimeout(() => setEditSaved(false), 2500);
    } catch (err) {
      console.error('Error saving track edit:', err);
    } finally {
      setEditSaving(false);
    }
  }, [currentTrack?.id, editFields]);

  const handleSubmitComment = useCallback(async () => {
    if (!newComment.trim() || !user || !currentTrack) return;
    setIsSubmittingComment(true);
    try {
      const trackId = currentTrack.id.includes('-track-') ? (currentTrack as any).workId || currentTrack.id : currentTrack.id;
      await addTrackComment({
        trackId,
        userId: user.uid,
        userName: user.displayName || 'Anonymous',
        userImageUrl: user.photoURL || '',
        content: newComment.trim(),
        timestampSeconds: pinCommentToTime ? Math.floor(currentTime) : undefined,
      });
      setNewComment('');
    } catch (err) {
      console.error('Error submitting comment:', err);
    } finally {
      setIsSubmittingComment(false);
    }
  }, [newComment, user, currentTrack, currentTime, pinCommentToTime]);

  const isYouTube = currentTrack?.youtubeId || currentTrack?.mediaType === "youtube";
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressChange = (value: number[]) => {
    seek((value[0] / 100) * duration);
  };
  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0] / 100);
  };
  const handleClosePlayer = () => {
    setActive(false);
    setIsSheetOpen(false);
  };
  const handleToggleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSheetOpen) {
      setIsSheetOpen(false);
    } else {
      const newActive = !isActive;
      setActive(newActive);
      if (!newActive) setIsSheetOpen(false);
    }
  };

  // Listen for chat opening — close the player sheet when chat opens on mobile
  useEffect(() => {
    const handler = () => setIsSheetOpen(false);
    window.addEventListener('close-player-sheet', handler);
    return () => window.removeEventListener('close-player-sheet', handler);
  }, []);

  // Hero video click (and anything else) can pop the full player window open
  useEffect(() => {
    const handler = () => setIsSheetOpen(true);
    window.addEventListener('open-player-sheet', handler);
    return () => window.removeEventListener('open-player-sheet', handler);
  }, []);

  const VolumeIcon = isMuted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const layoutPrefs = useOptionalUserLayoutPrefs();
  const [volumeOpen, setVolumeOpen] = useState(false);
  const volumeWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!volumeOpen) return;
    const close = (e: Event) => { if (!volumeWrapRef.current?.contains(e.target as Node)) setVolumeOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setVolumeOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); };
  }, [volumeOpen]);
  const RepeatIcon = repeat === "one" ? Repeat1 : Repeat;

  // trackImages: full array for slideshow — falls back to track-level images or single imageUrl.
  // Only honour resolvedImages when its trackId matches the currently-playing
  // track. During the one-render window between currentTrack.id changing and
  // the effect firing, this drops the previous track's resolved set on the
  // floor and renders from the new currentTrack's own optimistic imageUrl —
  // killing the wrong-thumbnail flash Gordon reported.
  const resolvedForCurrent = resolvedImages.trackId === currentTrack?.id ? resolvedImages.urls : [];
  const ownImages: string[] = resolvedForCurrent.length > 0
    ? resolvedForCurrent
    : ((currentTrack as any)?.images as string[] | undefined)?.filter(Boolean) || (currentTrack?.imageUrl ? [currentTrack.imageUrl] : []);
  // Fallback: if the current track carries no art of its own, borrow its OWN
  // queue entry's cover, or a cover from the same release by the same artist
  // (Gordon: "all of a sudden a track without thumbnail"). Never any other
  // item's — "the first queued cover" put a Shuba-K cover on a Chris Twice
  // track (Gordon 2026-11-01).
  const sameRelease = (q: typeof queue[number]) =>
    !!currentTrack?.album && q.album === currentTrack.album && (q.artist || '') === (currentTrack.artist || '');
  const queueCover =
    (currentTrack?.id ? queue.find(q => q.id === currentTrack.id && !!q.imageUrl)?.imageUrl : '')
    || (queue[queueIndex]?.id === currentTrack?.id ? queue[queueIndex]?.imageUrl : '')
    || queue.find(q => !!q.imageUrl && sameRelease(q))?.imageUrl
    || '';
  const trackImages: string[] = ownImages.length > 0 ? ownImages : (queueCover ? [queueCover] : []);
  // trackImage: first image as a string — for non-slideshow contexts (AddToPlaylist, QRShareButton, etc.)
  const trackImage = trackImages[0] || "";
  const trackTitle = currentTrack?.title || "No track selected";
  const trackArtist = currentTrack?.artist || "";
  const trackAlbum = currentTrack?.album || "";
  // Artist link resolution — prefer the explicit slug, then the artistId
  // (which the /artists/[slug] route also accepts), then a name-based slug.
  // Per Gordon: "why isn't the artist name a link back to artist page
  // anymore???? please fix, everything possible should be linked."
  // Previously this required artistSlug exclusively, which many tracks
  // don't have populated yet.
  const artistHref = (() => {
    if (!currentTrack) return null;
    const ct = currentTrack as any;
    // Authoritative link resolved from the work/video doc's artistId — prevents
    // videos (no artistSlug/artistId on the track) from slugifying a name that
    // doesn't resolve and bouncing to the all-artists page.
    if (resolvedArtistLink) return resolvedArtistLink;
    if (ct.artistSlug) return `/artists/${ct.artistSlug}`;
    if (ct.artistId) return `/artists/${ct.artistId}`;
    if (currentTrack.artist && typeof currentTrack.artist === 'string') {
      // Slugify the name as a last resort — the artist page tolerates
      // both slug + id, so a name-based fallback at least lands on a
      // searchable URL.
      const slug = String(currentTrack.artist).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      return slug ? `/artists/${slug}` : null;
    }
    return null;
  })();
  // Full artist display: "Primary Artist feat. Featured1, Featured2"
  // Prefer resolvedFeaturedArtists (from Firestore) — falls back to what the caller passed in
  const effectiveFeaturedArtists =
    resolvedFeaturedArtists.length > 0
      ? resolvedFeaturedArtists
      : (currentTrack?.featuredArtists ?? []);
  const trackArtistDisplay = trackArtist
    ? effectiveFeaturedArtists.length
      ? `${trackArtist} feat. ${effectiveFeaturedArtists.join(", ")}`
      : trackArtist
    : "";

  // Sorted queue with original indices preserved
  const sortedQueue = useMemo(() => {
    const indexed = queue.map((t, i) => ({ track: t, originalIndex: i }));
    switch (queueSort) {
      case "az":
        return [...indexed].sort((a, b) => a.track.title.localeCompare(b.track.title));
      case "za":
        return [...indexed].sort((a, b) => b.track.title.localeCompare(a.track.title));
      case "artist":
        return [...indexed].sort((a, b) =>
          (a.track.artist || "").localeCompare(b.track.artist || "")
        );
      default:
        return indexed; // original order
    }
  }, [queue, queueSort]);

  const runQueueAction = async () => {
    if (!queueActionConfirm) return;
    const { id, action, title } = queueActionConfirm;
    setQueueActionBusy(true);
    try {
      if (action === "erase") {
        await deleteWork(id);
        toast({ title: "Track erased", description: `"${title}" was permanently deleted.` });
      } else {
        await updateWork(id, { adminHidden: action === "hide" } as any);
        setQueueHiddenIds(prev => {
          const next = new Set(prev);
          if (action === "hide") next.add(id); else next.delete(id);
          return next;
        });
        toast({ title: action === "hide" ? "Track hidden" : "Track unhidden", description: title });
      }
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err?.message || String(err) });
    } finally {
      setQueueActionBusy(false);
      setQueueActionConfirm(null);
    }
  };

  // Desktop bar: ONE play/pause button; press and hold it for a pull-up menu
  // with the other transport controls (Gordon 2026-10-30).
  const [transportMenuOpen, setTransportMenuOpen] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldRef = useRef(false);
  const transportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!transportMenuOpen) return;
    const close = (e: PointerEvent) => { if (!transportRef.current?.contains(e.target as Node)) setTransportMenuOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setTransportMenuOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', esc); };
  }, [transportMenuOpen]);

  const commonPlaybackControls = (size: "sm" | "default" = "default", desktopBar = false, omitMain = false) => {
    const iconSize = (size === "sm" || desktopBar) ? "h-4 w-4" : "h-5 w-5";
    // Play/pause reads TWO steps larger than the other transport icons and in
    // fluorescent green (Gordon: "make play icon in media player larger and
    // flourecent green play icon").
    const mainIconSize = (size === "sm" || desktopBar) ? "h-6 w-6" : "h-7 w-7";
    const mainIconColor = "text-[#39FF14] fill-[#39FF14]";
    const btnSize = (size === "sm" || desktopBar) ? "h-7 w-7" : "h-auto w-auto";
    const mainBtnSize = desktopBar ? "h-8 w-8 rounded-md" : size === "sm" ? "h-9 w-9 rounded-full" : "h-10 w-10 rounded-full";
    return (
      <>
        <HelpBubble title="Shuffle" description="Play tracks in your queue in a random order. Tap again to disable." learnMoreAnchor="media-player" side="top">
        <Button variant="ghost" size="icon"
          aria-label={shuffle ? "Disable shuffle" : "Enable shuffle"}
          aria-pressed={shuffle}
          className={cn("text-muted-foreground hover:text-primary", btnSize, shuffle && "text-primary")}
          onClick={toggleShuffle}>
          <Shuffle className={iconSize} />
        </Button>
        </HelpBubble>
        <HelpBubble title="Previous track" description="Go back to the previous track in your queue, or restart the current track." learnMoreAnchor="media-player" side="top">
        <Button variant="ghost" size="icon"
          aria-label="Previous track"
          className={cn("text-muted-foreground hover:text-primary", btnSize)}
          onClick={skipPrev}>
          <SkipBack className={iconSize} />
        </Button>
        </HelpBubble>
        {!omitMain && <HelpBubble title="Play / Pause" description="Play or pause the current track. Click any song card on the page to start playing." learnMoreAnchor="media-player" aiQuestion="How does the Breakin' Bread media player work?" side="top">
        <Button variant="default" size="icon"
          aria-label={isPlaying ? "Pause" : "Play"}
          className={cn(
            // Fixed dark chrome + light outline regardless of site theme —
            // NOT bg-primary, which flips to near-white in dark mode
            // (--primary: 0 0% 97% in .dark) and made this a glaring white
            // circle (Gordon 2026-09-21: "why does the play button ... have
            // a white background in dark mode??? just light outline and
            // dark background and the green play button"). Matches the
            // already-hardcoded fluorescent green icon (mainIconColor) —
            // this control's look is deliberately theme-independent.
            "bg-black border-2 border-white/50 hover:border-white hover:bg-black/80",
            mainBtnSize,
            audioError && "bg-destructive border-destructive hover:bg-destructive/90"
          )}
          onClick={togglePlay}
          title={audioError || undefined}>
          {isLoading ? <Loader2 className={cn(mainIconSize, "animate-spin")} /> :
            audioError ? <AlertCircle className={mainIconSize} /> :
            isPlaying ? <Pause className={cn(mainIconSize, mainIconColor)} /> : <Play className={cn(mainIconSize, mainIconColor)} />}
        </Button>
        </HelpBubble>}
        <HelpBubble title="Next track" description="Skip to the next track in your queue." learnMoreAnchor="media-player" side="top">
        <Button variant="ghost" size="icon"
          aria-label="Next track"
          className={cn("text-muted-foreground hover:text-primary", btnSize)}
          onClick={skipNext}>
          <SkipForward className={iconSize} />
        </Button>
        </HelpBubble>
        <HelpBubble title="Repeat" description="Cycle through repeat modes: off → repeat all → repeat one track." learnMoreAnchor="media-player" side="top">
        <Button variant="ghost" size="icon"
          aria-label={repeat === "off" ? "Enable repeat" : repeat === "one" ? "Repeat one track" : "Disable repeat"}
          aria-pressed={repeat !== "off"}
          className={cn("text-muted-foreground hover:text-primary", btnSize, repeat !== "off" && "text-primary")}
          onClick={cycleRepeat}>
          <RepeatIcon className={iconSize} />
        </Button>
        </HelpBubble>
        {/* Stream quality toggle: cycle auto → streaming → lossless */}
        <Button
          variant="ghost"
          size="icon"
          title={
            streamQuality === 'lossless'  ? 'WAV (lossless) — click for auto'
            : streamQuality === 'streaming' ? 'M4A (streaming) — click for lossless'
            : 'Auto quality — click for M4A'
          }
          className={cn(btnSize,
            streamQuality === 'lossless'  && "text-cyan-400 hover:text-cyan-300",
            streamQuality === 'streaming' && "text-blue-400 hover:text-blue-300",
            streamQuality === 'auto'      && "text-muted-foreground hover:text-foreground"
          )}
          onClick={() => setStreamQuality(
            streamQuality === 'auto'      ? 'streaming'
            : streamQuality === 'streaming' ? 'lossless'
            : 'auto'
          )}
        >
          <span className="text-[9px] font-bold leading-none">
            {streamQuality === 'lossless' ? 'WAV' : streamQuality === 'streaming' ? 'M4A' : 'AUTO'}
          </span>
        </Button>
      </>
    );
  };

  const commonProgressSlider = (isMobileView = false) => {
    const sliderClass = isMobileView
      ? "[&>span:first-child]:h-1 [&>span:last-child]:hidden [&>span:first-child_span]:h-1"
      : "[&>span:first-child]:h-1.5 [&>span:last-child]:hidden [&>span:first-child_span]:h-1.5";
    return (
      <div className="flex w-full items-center gap-2">
        <p className="text-xs text-muted-foreground w-10 text-right">{formatTime(currentTime)}</p>
        <Slider
          value={[progress]} max={100} step={0.1}
          aria-label="Track progress"
          aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
          className={cn("flex-grow", sliderClass, "[&>span:last-child]:hidden")}
          onValueChange={handleProgressChange}
        />
        <p className="text-xs text-muted-foreground w-10 text-left">{formatTime(duration)}</p>
      </div>
    );
  };

  // ─── Mini bottom bar controls (unchanged) ───────────────────────────────────

  const desktopPlayerControls = (
    // ONE row at every desktop/tablet width, as tall as the track thumbnail —
    // the same 50px as the menu, chat and player buttons (Gordon 2026-10-30):
    // thumbnail · title/artist · play (hold → other controls) · waveform ·
    // volume · favorite · playlist · queue; the player toggle sits far right.
    <div className="flex items-center w-full gap-3 h-[var(--player-height)] pr-[calc(var(--sidebar-width-icon)_+_0.5rem)]">
      <button
        type="button"
        data-player-sheet-toggle=""
        className="flex items-center gap-3 min-w-0 flex-[0_1_300px] text-left"
        onClick={() => { window.dispatchEvent(new Event('close-vu-popup')); setIsSheetOpen(prev => !prev); }}
        aria-label={trackTitle}
      >
        <span className={cn("relative flex-shrink-0 h-[var(--player-height)] w-[var(--player-height)] overflow-hidden bg-muted flex items-center justify-center", isPlaying && "bb-playing-glow")}>
          {trackImages.length > 0 ? (
            <span className="absolute inset-0 will-change-transform" style={{ transform: 'scale(var(--bb-thumb-pulse,1))' }}>
              <SlideshowImage images={trackImages} alt={trackTitle} interval={4000} sizes="64px" />
            </span>
          ) : isYouTube ? <Video className="h-5 w-5 text-muted-foreground" /> :
            currentTrack?.mediaType === "radio" ? <Radio className="h-5 w-5 text-muted-foreground" /> :
            <Music2 className="h-5 w-5 text-muted-foreground" />}
        </span>
        <span className="min-w-0 flex-1">
          <MarqueeText text={trackTitle} className="capitalize text-sm font-semibold leading-tight" />
          {audioError ? (
            <span className="block text-xs text-destructive truncate" title={audioError}>⚠ {audioError}</span>
          ) : (
            <span className="block text-xs text-muted-foreground truncate leading-tight">
              {artistHref && trackArtist
                ? <Link href={artistHref} onClick={(e) => { e.stopPropagation(); setIsSheetOpen(false); }} className="hover:text-primary hover:underline">{trackArtistDisplay}</Link>
                : trackArtistDisplay}
            </span>
          )}
        </span>
      </button>

      {/* Play / pause — press and hold for shuffle, previous, next, repeat, quality */}
      <div ref={transportRef} className="relative flex-shrink-0">
        <Button
          variant="default" size="icon"
          aria-label={isPlaying ? "Pause" : "Play"}
          aria-haspopup="menu"
          aria-expanded={transportMenuOpen}
          title={audioError || "Hold for more controls"}
          className={cn(
            "h-9 w-9 rounded-md bg-black border-2 border-white/50 hover:border-white hover:bg-black/80",
            audioError && "bg-destructive border-destructive hover:bg-destructive/90",
          )}
          onPointerDown={() => {
            heldRef.current = false;
            holdTimer.current = setTimeout(() => { heldRef.current = true; setTransportMenuOpen(true); }, 450);
          }}
          onPointerUp={() => { if (holdTimer.current) clearTimeout(holdTimer.current); }}
          onPointerLeave={() => { if (holdTimer.current) clearTimeout(holdTimer.current); }}
          onContextMenu={(e) => { e.preventDefault(); setTransportMenuOpen(true); }}
          onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); setTransportMenuOpen(true); } }}
          onClick={() => { if (heldRef.current) { heldRef.current = false; return; } togglePlay(); }}
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
            audioError ? <AlertCircle className="h-5 w-5" /> :
            isPlaying ? <Pause className="h-5 w-5 text-[#39FF14] fill-[#39FF14]" /> : <Play className="h-5 w-5 text-[#39FF14] fill-[#39FF14]" />}
        </Button>
        {transportMenuOpen && (
          <div role="menu" className="absolute bottom-[calc(100%_+_0.5rem)] left-1/2 z-[110] -translate-x-1/2 flex items-center gap-1 rounded-xl border border-border bg-card p-1.5 shadow-2xl">
            {commonPlaybackControls("default", true, true)}
          </div>
        )}
      </div>

      {/* Waveform timeline */}
      <div className="flex-1 min-w-0" data-help-title={tp('waveformHelpTitle')} data-help-description={tp('waveformHelpDesc')}>
        {!isYouTube && currentTrack?.audioUrl
          ? (
            <TrackWaveform
              audioUrl={currentTrack.audioUrl}
              durationSeconds={currentTrack.duration}
              comments={[]}
              deferUntilPlayed
              // The waveform grows with the chosen player height (Settings → Layout);
              // the cover does too, every other button keeps its size (Gordon 2026-11-01).
              height={WAVE_HEIGHT[layoutPrefs?.playerSize ?? 'compact']}
              showVu={false}
              showStylePicker={false}
              defaultStyle="bars"
              className="w-full"
            />
          )
          : commonProgressSlider()}
      </div>

      <div className="flex flex-shrink-0 items-center gap-1">
        {/* Volume (Gordon 2026-11-01): one icon; tap opens a vertical slider above
            it — the device's own volume does most of the work, this is for
            balancing BB against other apps. Mute lives in the pop-up. */}
        <div ref={volumeWrapRef} className="relative" data-help-title={tp('volumeHelpTitle')} data-help-description={tp('volumeHelpDesc')}>
          <Button variant="ghost" size="icon" aria-label={tp('volumeOpen')} aria-expanded={volumeOpen} aria-haspopup="dialog"
            className={cn("text-muted-foreground hover:text-primary h-8 w-8", volumeOpen && "text-primary")} onClick={() => setVolumeOpen(o => !o)}>
            <VolumeIcon className="h-4 w-4" />
          </Button>
          {volumeOpen && (
            <div role="dialog" aria-label={tp('volumeOpen')}
              className="absolute bottom-full left-1/2 z-[110] mb-2 flex -translate-x-1/2 flex-col items-center gap-2 rounded-xl border border-border bg-background/95 px-2 py-3 shadow-xl backdrop-blur-md">
              <span className="text-2xs font-semibold tabular-nums">{isMuted ? 0 : Math.round(volume * 100)}%</span>
              <Slider
                orientation="vertical"
                value={[isMuted ? 0 : volume * 100]} max={100} step={1}
                aria-label="Volume"
                aria-valuetext={isMuted ? "Muted" : `${Math.round(volume * 100)}%`}
                className="h-[21rem] max-h-[60vh]"
                onValueChange={handleVolumeChange}
              />
              <Button variant="ghost" size="icon" aria-label={isMuted ? "Unmute" : "Mute"} aria-pressed={isMuted}
                className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={toggleMute}>
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </Button>
            </div>
          )}
        </div>
        <Button variant="ghost" size="icon" aria-label={common(isFavoritedState ? "removeFromFavorites" : "addToFavorites")} aria-pressed={isFavoritedState} className="h-8 w-8 text-muted-foreground hover:text-primary"
          onClick={handleToggleFavorite}>
          <Heart className={cn("h-4 w-4", isFavoritedState && "fill-primary text-primary")} />
        </Button>
        {currentTrack && (
          <AddToPlaylist
            track={{
              trackId: currentTrack.id,
              title: currentTrack.title,
              artist: currentTrack.artist || '',
              imageUrl: currentTrack.imageUrl,
              audioUrl: currentTrack.audioUrl,
              youtubeId: currentTrack.youtubeId,
              duration: currentTrack.duration,
            }}
            className="text-muted-foreground hover:text-primary"
          />
        )}
        <Button variant="ghost" size="icon" aria-label="Open queue" className="text-muted-foreground hover:text-primary h-8 w-8" onClick={() => { window.dispatchEvent(new Event('close-vu-popup')); setIsSheetOpen(true); }}>
          <ListMusic className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );

  const compactMobilePlayerBar = (
    // gap-0: thumbnail is flush against the left edge (gap only applies between items, but
    // gap-0 makes intent explicit). ml-2 is added on the text section instead.
    // mr-3 on the button group adds breathing room before the bar's own right edge
    // (the chat FAB now sits at left-0, on the opposite side).
    <div className="flex items-center w-full gap-0 p-0 h-[56px] overflow-hidden">
      {/* Thumbnail — no leading gap, fills full bar height */}
      {trackImages.length > 0 ? (
        <div data-player-sheet-toggle="" className={cn("mobile-player-bar-thumb relative flex-shrink-0 w-14 h-[56px] overflow-hidden bg-muted cursor-pointer border border-border", isPlaying && "bb-playing-glow")}
          onClick={() => {
            window.dispatchEvent(new Event('close-vu-popup'));
            setIsSheetOpen(prev => {
              if (!prev) window.dispatchEvent(new Event('close-sidebar-and-chat'));
              return !prev;
            });
          }}>
          {/* Inner wrapper zooms with the music (--bb-thumb-pulse) inside the
              clipped frame — signals the thumb is live and tappable */}
          <div className="absolute inset-0 will-change-transform" style={{ transform: 'scale(var(--bb-thumb-pulse,1))' }}>
            <SlideshowImage images={trackImages} alt={trackTitle} interval={4000} sizes="64px" />
          </div>
        </div>
      ) : (
        <div data-player-sheet-toggle="" className={cn("mobile-player-bar-thumb relative flex-shrink-0 w-14 h-[56px] bg-muted flex items-center justify-center cursor-pointer border border-border", isPlaying && "bb-playing-glow")}
          onClick={() => {
            window.dispatchEvent(new Event('close-vu-popup'));
            setIsSheetOpen(prev => {
              if (!prev) window.dispatchEvent(new Event('close-sidebar-and-chat'));
              return !prev;
            });
          }}>
          <div className="will-change-transform" style={{ transform: 'scale(var(--bb-thumb-pulse,1))' }}>
            <Music2 className="h-6 w-6 text-muted-foreground" />
          </div>
        </div>
      )}

      {/* Track info + progress — ml-2 gives the gap that gap-2 previously provided */}
      <div data-player-sheet-toggle="" className="ml-2 min-w-0 flex-grow" onClick={() => {
        window.dispatchEvent(new Event('close-vu-popup'));
        setIsSheetOpen(prev => {
          if (!prev) window.dispatchEvent(new Event('close-sidebar-and-chat'));
          return !prev;
        });
      }}>
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-grow overflow-hidden">
            {/* Marquee title: scrolls on hover (desktop) / every 6s (mobile) */}
            <div className="flex items-center gap-1">
              <MarqueeText text={trackTitle} className="capitalize text-sm font-medium min-w-0" />
              {trackExplicit && <ExplicitBadge explicit size={12} className="flex-shrink-0" />}
            </div>
            <div className="flex justify-between items-baseline">
              {artistHref && trackArtist ? (
                <Link href={artistHref} onClick={() => setIsSheetOpen(false)} className="text-xs text-muted-foreground truncate hover:text-primary transition-colors hover:underline">{trackArtistDisplay}</Link>
              ) : (
                <p className="text-xs text-muted-foreground truncate">{trackArtistDisplay}</p>
              )}
              <p className="text-xs text-muted-foreground pr-2">{formatTime(currentTime)}/{formatTime(duration)}</p>
            </div>
          </div>
        </div>
        <Slider
          value={[progress]} max={100} step={0.1}
          aria-label="Track progress"
          aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
          className="flex-grow [&>span:first-child]:h-1 [&>span:last-child]:hidden [&>span:first-child_span]:h-1"
          onValueChange={handleProgressChange}
        />
      </div>

      {/* Controls — mr-3 adds space before the bar's own right edge */}
      <div className="flex items-center gap-0 flex-shrink-0 mr-3">
        <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground"
          onClick={(e) => { e.stopPropagation(); handleToggleFavorite(); }}>
          <Heart className={cn("h-4 w-4", isFavoritedState && "fill-primary text-primary")} />
        </Button>
        {/* Green by default (Gordon: "make the play button green as default") */}
        <Button variant="ghost" size="icon" className="h-9 w-9 text-green-500"
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}>
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
            isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
        </Button>
      </div>
    </div>
  );

  // ─── Queue list (shared between mobile single-col and desktop right panel) ──


  const queueTabContent = (
    <div className="mt-3">
      {/* Sort + view controls */}
      <div className="flex items-center gap-2 mb-3">
        <Select value={queueSort} onValueChange={(v) => setQueueSort(v as QueueSort)}>
          <SelectTrigger className="h-8 text-xs flex-1 min-w-0">
            <ArrowUpDown className="h-3 w-3 mr-1.5 flex-shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="original">Track Order</SelectItem>
            <SelectItem value="az">A → Z</SelectItem>
            <SelectItem value="za">Z → A</SelectItem>
            <SelectItem value="artist">By Artist</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex border rounded-md overflow-hidden flex-shrink-0">
          <Button
            variant="ghost" size="icon"
            className={cn("h-8 w-8 rounded-none", queueView === "list" && "bg-secondary")}
            onClick={() => setQueueView("list")}>
            <LayoutList className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className={cn("h-8 w-8 rounded-none", queueView === "compact" && "bg-secondary")}
            onClick={() => setQueueView("compact")}>
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {queue.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">{tp('queueEmpty')}</p>
      ) : (
        <>
          {/* What this queue is: the album/playlist you chose, or autoplay
              after it ended (Andrés 2026-11-01: autoplay played a new song
              while this still named the finished album). */}
          {(queueContext?.kind === 'autoplay' || currentTrack?.album) && (
            <div className="flex items-center gap-2 px-2 pb-2 mb-1 border-b border-border/50">
              {queueContext?.kind === 'autoplay'
                ? <Radio className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                : <Disc className="h-3.5 w-3.5 text-primary" aria-hidden="true" />}
              <p className="text-xs font-medium text-muted-foreground min-w-0 truncate">
                {queueContext?.kind === 'autoplay'
                  ? (queueContext.basedOn
                    ? tp.rich('queueAutoplayFrom', { name: queueContext.basedOn, b: (c) => <span className="text-foreground">{c}</span> })
                    : tp('queueAutoplay'))
                  : tp.rich('queuePlayingFrom', { name: currentTrack?.album || '', b: (c) => <span className="text-foreground">{c}</span> })}
              </p>
              <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">{tp('queueTrackCount', { count: queue.length })}</span>
            </div>
          )}
          <div className="space-y-0.5">
            {sortedQueue.map(({ track: item, originalIndex: i }) => (
              <div
                key={item.id + "-" + i}
                ref={i === queueIndex ? currentQueueRowRef : undefined}
                aria-current={i === queueIndex ? "true" : undefined}
                className={cn(
                  "flex items-center gap-2 rounded-md hover:bg-secondary/50 cursor-pointer group",
                  queueView === "list" ? "p-2" : "px-2 py-1",
                  i === queueIndex && "bg-primary/10 border border-primary/20"
                )}
                onClick={() => playTracks(queue, i)}
              >
                <span className="text-xs text-muted-foreground w-5 text-center flex-shrink-0">{i + 1}</span>

                {queueView === "list" && (
                  item.imageUrl ? (
                    <Image src={item.imageUrl} width={36} height={36} alt={item.title}
                      className="rounded flex-shrink-0" />
                  ) : (
                    <div className="w-9 h-9 bg-muted rounded flex items-center justify-center flex-shrink-0">
                      <Music2 className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )
                )}

                <div className="min-w-0 flex-1">
                  <MarqueeText text={item.title} className={cn("capitalize text-sm font-medium", i === queueIndex && "text-primary")} />
                  {queueView === "list" && (
                    <p className="text-xs text-muted-foreground truncate">{item.artist}</p>
                  )}
                </div>

                {(item as any).fileFormat && (
                  <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
                    {(item as any).fileFormat}
                  </span>
                )}

                {item.duration && (
                  <p className="text-xs text-muted-foreground flex-shrink-0">{formatTime(item.duration)}</p>
                )}

                {item.id && (
                  // One size, one shape, a gap between (Andrés 2026-11-01: the
                  // download button was bigger than share and they touched).
                  <div onClick={e => e.stopPropagation()} className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Per-track PWYW download — same dialog as the release
                        page, now available right in the player window
                        (Gordon: "need to be able to download tracks in the
                        media player window for each track"). */}
                    {!isLinkedVideo(item) && <DownloadButton
                      compact
                      className="h-8 w-8 rounded-[var(--btn-radius)] bb-hover-reveal"
                      item={{
                        id: item.id,
                        title: item.title,
                        artist: item.artist || '',
                        coverArtUrl: item.imageUrl || '',
                        downloadType: 'pwyw',
                        downloadMinPrice: 0,
                        allowDownloads: true,
                        audioUrl: item.audioUrl || '',
                        hiResUrl: (item as any).hiResUrl || (item as any).wavUrl,
                      }}
                    />}
                    <ShareButton
                      // Lead with the artist name so the bold first line of
                      // every share card reads "Artist — Title".
                      title={item.artist ? `${item.artist} — ${item.title}` : item.title}
                      url={`/music/${item.id}`}
                      variant="outline"
                      size="icon"
                      iconOnly
                      className="h-8 w-8 bb-hover-reveal"
                    />
                    {isSuperAdmin && (
                      <>
                        <Button
                          variant="ghost" size="icon"
                          className={cn(
                            "h-8 w-8 bb-hover-reveal",
                            queueHiddenIds.has(item.id) && "text-amber-500 opacity-100 hover:text-amber-500"
                          )}
                          title={queueHiddenIds.has(item.id) ? "Unhide track" : "Hide track"}
                          onClick={() => setQueueActionConfirm({
                            id: item.id, title: item.title,
                            action: queueHiddenIds.has(item.id) ? "unhide" : "hide",
                          })}
                        >
                          {queueHiddenIds.has(item.id) ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="h-8 w-8 bb-hover-reveal text-muted-foreground hover:text-destructive"
                          title="Erase track"
                          onClick={() => setQueueActionConfirm({ id: item.id, title: item.title, action: "erase" })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // ─── Related thumbnails (from rest of queue) ─────────────────────────────────
  const relatedTracks = queue.filter((_, i) => i !== queueIndex).slice(0, 12);

  const relatedGrid = relatedTracks.length > 0 ? (
    <div className="mt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {currentTrack?.album ? "Album Tracks" : "Up Next"}
      </h4>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {relatedTracks.map((track, i) => (
          <div
            key={track.id + "-rel-" + i}
            className="cursor-pointer group"
            onClick={() => {
              const idx = queue.findIndex(q => q.id === track.id);
              if (idx >= 0) playTracks(queue, idx);
            }}
          >
            <div className="relative aspect-square rounded-md overflow-hidden bg-muted mb-1">
              {track.imageUrl ? (
                <Image src={track.imageUrl} alt={track.title} fill
                  className="object-cover group-hover:scale-105 transition-transform" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Music2 className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Play className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <p className="text-[10px] font-medium truncate leading-tight">{track.title}</p>
            {track.artistSlug ? (
              <Link href={`/artists/${track.artistSlug}`} onClick={() => setIsSheetOpen(false)} className="text-[9px] text-muted-foreground truncate block hover:text-primary transition-colors">{track.artist}</Link>
            ) : (
              <p className="text-[9px] text-muted-foreground truncate">{track.artist}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  // ─── Related by artist — filter out tracks already in the current queue ─────
  // If every track by this artist is already queued (e.g. full album loaded),
  // this collapses to [] and the section hides completely.
  const relatedNotInQueue = useMemo(() => {
    const queueIds = new Set(queue.map(t => t.id));
    return relatedByArtist.filter(t => !queueIds.has(t.id));
  }, [relatedByArtist, queue]);

  // ─── Related by artist / same genre grid ─────────────────────────────────────
  const relatedByArtistGrid = relatedNotInQueue.length > 0 ? (
    <div className="mt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {currentTrack?.artist ? `More from ${currentTrack.artist}` : "More Like This"}
      </h4>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {relatedNotInQueue.slice(0, 12).map((track, i) => (
          <div
            key={track.id + "-art-" + i}
            className="cursor-pointer group"
            onClick={() => {
              // Append only genuinely new tracks to the queue, then play the chosen one
              playTracks([...queue, ...relatedNotInQueue], queue.length + i);
            }}
          >
            <div className="relative aspect-square rounded-md overflow-hidden bg-muted mb-1">
              {track.imageUrl ? (
                <Image src={track.imageUrl} alt={track.title} fill
                  className="object-cover group-hover:scale-105 transition-transform" />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Music2 className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <Play className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <p className="text-[10px] font-medium truncate leading-tight">{track.title}</p>
            {track.artistSlug ? (
              <Link href={`/artists/${track.artistSlug}`} onClick={() => setIsSheetOpen(false)} className="text-[9px] text-muted-foreground truncate block hover:text-primary transition-colors">{track.artist}</Link>
            ) : (
              <p className="text-[9px] text-muted-foreground truncate">{track.artist}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  // ─── Edit tab content ─────────────────────────────────────────────────────────
  const [lyricsGenerating, setLyricsGenerating] = useState(false);

  // ─── In-player lyrics translation ────────────────────────────────────────────
  const PLAYER_LANGUAGES = [
    { code: 'es', name: 'Spanish' }, { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' }, { code: 'pt', name: 'Portuguese' },
    { code: 'it', name: 'Italian' }, { code: 'sv', name: 'Swedish' },
    { code: 'no', name: 'Norwegian' }, { code: 'da', name: 'Danish' },
    { code: 'nl', name: 'Dutch' }, { code: 'pl', name: 'Polish' },
    { code: 'ru', name: 'Russian' }, { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' }, { code: 'zh', name: 'Chinese' },
    { code: 'ar', name: 'Arabic' }, { code: 'hi', name: 'Hindi' },
    { code: 'tr', name: 'Turkish' }, { code: 'en', name: 'English' },
  ];
  const [playerLang, setPlayerLang] = useState('es');
  const [playerTranslations, setPlayerTranslations] = useState<Record<string, string>>({});
  const [playerTranslating, setPlayerTranslating] = useState(false);
  const [showPlayerTranslation, setShowPlayerTranslation] = useState(false);
  // Reset translations when track changes
  useEffect(() => {
    setPlayerTranslations({});
    setShowPlayerTranslation(false);
  }, [currentTrack?.id]);

  const handlePlayerTranslate = useCallback(async () => {
    const lyrics = currentTrack?.lyrics || editFields.lyrics;
    if (!lyrics || playerTranslating) return;
    if (playerTranslations[playerLang]) { setShowPlayerTranslation(true); return; }
    setPlayerTranslating(true);
    try {
      const langName = PLAYER_LANGUAGES.find(l => l.code === playerLang)?.name || playerLang;
      const res = await fetchWithAuth('/api/ai/translate-lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lyrics, targetLanguage: playerLang, targetLanguageName: langName, title: currentTrack?.title, artist: currentTrack?.artist }),
      });
      if (!res.ok) throw new Error('Translation failed');
      const data = await res.json();
      setPlayerTranslations(prev => ({ ...prev, [playerLang]: data.translatedLyrics }));
      setShowPlayerTranslation(true);
    } catch { /* show toast silently */ }
    finally { setPlayerTranslating(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.lyrics, editFields.lyrics, currentTrack?.id, playerLang, playerTranslations, playerTranslating]);

  // ─── AI generate lyrics from player (no-lyrics state) ────────────────────────
  const [playerLyricsGenerating, setPlayerLyricsGenerating] = useState(false);
  const handlePlayerGenerateLyrics = useCallback(async () => {
    if (!currentTrack?.id || playerLyricsGenerating) return;
    // AI lyrics are TRANSCRIBED from the actual vocals — the route needs the
    // audio file. Sending only title/artist (the old behaviour) always errored
    // and the error was swallowed, so the button silently did nothing.
    const audioUrl = (currentTrack as any).audioUrl || (currentTrack as any).mp3Url || '';
    if (!audioUrl) {
      toast({ title: 'No audio to transcribe', description: 'AI lyrics are transcribed from the track’s vocals — this track has no audio file.', variant: 'destructive' });
      return;
    }
    setPlayerLyricsGenerating(true);
    try {
      const res = await fetchWithAuth('/api/ai/generate-lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioUrl, transcribe: true, title: currentTrack.title, artist: currentTrack.artist }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        toast({ title: 'Lyrics generation failed', description: data.error || `Server error (${res.status})`, variant: 'destructive' });
        return;
      }
      const lyrics = (data.lyrics || '').trim();
      if (!lyrics || /^instrumental$/i.test(lyrics)) {
        toast({ title: 'No vocals detected', description: 'The track appears to be instrumental.' });
        return;
      }
      // Persist via the authenticated route so a contributor's transcription
      // saves too (works/{id} rules block a non-owner client write). Marked
      // `auto` → shows an "unverified" badge until a human corrects it.
      await fetchWithAuth('/api/works/lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workId: currentTrack.id, lyrics, auto: true }),
      }).catch(() => {});
      (currentTrack as any).lyrics = lyrics;
      (currentTrack as any).lyricsAuto = true;
      setLyricsMeta({ auto: true, verified: false });
      // editFields drives the lyrics panel display reliably (state → re-render).
      setEditFields(prev => ({ ...prev, lyrics }));
      toast({ title: 'Lyrics transcribed ✓' });
    } catch {
      toast({ title: 'Lyrics generation failed', description: 'Network error — please try again.', variant: 'destructive' });
    } finally {
      setPlayerLyricsGenerating(false);
    }
  }, [currentTrack, playerLyricsGenerating, toast]);

  // ─── Save edited lyrics/transcript (Lyrics tab inline editor) ────────────────
  // Persists ONLY the lyrics field so contributors can correct the transcript
  // without touching (or needing access to) the rest of the metadata form.
  const handleSaveLyrics = useCallback(async () => {
    if (!currentTrack?.id) return;
    setLyricsSaving(true);
    try {
      const lyrics = editFields.lyrics;
      // Save via the authenticated route so credited CONTRIBUTORS (not just the
      // owner/admin) can persist their edits — the works/{id} Firestore rule
      // would otherwise reject a non-owner write.
      const res = await fetchWithAuth('/api/works/lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workId: currentTrack.id, lyrics }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        toast({ title: 'Could not save lyrics', description: data.error || `Server error (${res.status})`, variant: 'destructive' });
        return;
      }
      (currentTrack as any).lyrics = lyrics;
      (currentTrack as any).lyricsAuto = false;
      setLyricsMeta({ auto: false, verified: true });
      setLyricsEditing(false);
      setLyricsSaved(true);
      setTimeout(() => setLyricsSaved(false), 2500);
      toast({ title: 'Lyrics saved ✓' });
    } catch (err) {
      console.error('Error saving lyrics:', err);
      toast({ title: 'Could not save lyrics', description: 'Network error — please try again.', variant: 'destructive' });
    } finally {
      setLyricsSaving(false);
    }
  }, [currentTrack, editFields.lyrics, toast]);

  // ─── Compute BPM + key once (free, client-side) when the player is open ───────
  // Runs only after the doc seed for THIS track shows no stored value, and only
  // once per track per session. Result is cached on the work doc so it never
  // recomputes across sessions/devices.
  useEffect(() => {
    if (!isSheetOpen) return;
    const t = currentTrack;
    if (!t?.id) return;
    const a = trackAnalysis;
    if (!a || a.trackId !== t.id) return;      // wait for the doc seed
    if (a.bpm || a.key) return;                 // already have it (stored)
    if (analyzedRef.current.has(t.id)) return;  // already attempted this session
    const audioUrl = (t as any).audioUrl || (t as any).mp3Url || '';
    if (!audioUrl) return;                       // no audio (e.g. YouTube) → skip
    analyzedRef.current.add(t.id);

    let cancelled = false;
    const ac = new AbortController();
    setTrackAnalysis({ trackId: t.id, bpm: 0, key: '', pending: true });
    (async () => {
      try {
        const buf = await decodeAudioFromUrl(audioUrl, ac.signal);
        if (cancelled) return;
        const { bpm, key, keyConfidence } = analyzeAudioBuffer(buf);
        if (cancelled) return;
        setTrackAnalysis({ trackId: t.id, bpm, key, pending: false });
        // Persist once (server only fills empty fields → no vandalism).
        fetchWithAuth('/api/works/analysis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workId: t.id, bpm, musicalKey: key, keyConfidence }),
        }).catch(() => {});
      } catch {
        if (!cancelled) setTrackAnalysis({ trackId: t.id, bpm: 0, key: '', pending: false });
      }
    })();
    return () => { cancelled = true; ac.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSheetOpen, currentTrack?.id, trackAnalysis]);

  const handleGenerateLyrics = useCallback(async () => {
    if (!currentTrack?.title) return;
    setLyricsGenerating(true);
    try {
      const res = await fetchWithAuth('/api/ai/generate-lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: currentTrack.title, artist: currentTrack.artist, genre: currentTrack.genre }),
      });
      const data = await res.json();
      if (data.lyrics) setEditFields(prev => ({ ...prev, lyrics: data.lyrics }));
    } catch (err) { console.error(err); }
    finally { setLyricsGenerating(false); }
  }, [currentTrack?.title, currentTrack?.artist, currentTrack?.genre]);

  const editTabContent = canEdit ? (
    <div className="mt-4 space-y-4 pb-6">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Edit Track</h4>
        <Button size="sm" onClick={handleSaveEdit} disabled={editSaving} className="h-7 px-3 text-xs">
          {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : editSaved ? <><Check className="h-3.5 w-3.5 mr-1" />Saved</>
            : <><Save className="h-3.5 w-3.5 mr-1" />Save</>}
        </Button>
      </div>

      <div className="space-y-3">
        {/* Cover art preview + URL */}
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Cover Art URL</Label>
          <div className="flex gap-2">
            {editFields.coverArtUrl && (
              <div className="relative flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-muted">
                <Image src={editFields.coverArtUrl} alt="cover" fill className="object-cover" />
              </div>
            )}
            <Input
              value={editFields.coverArtUrl}
              onChange={e => {
                setEditFields(prev => ({ ...prev, coverArtUrl: e.target.value }));
                if (e.target.value && currentTrack?.id) setResolvedImages({ trackId: currentTrack.id, urls: [e.target.value] });
              }}
              className="h-8 text-sm"
              placeholder="https://…"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Title</Label>
          <Input value={editFields.title} onChange={e => setEditFields(prev => ({ ...prev, title: e.target.value }))} className="h-8 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Artist</Label>
          {/* DB-driven: search existing artists so quick-edits don't fork a duplicate name */}
          <ArtistInput
            value={editFields.artist}
            onChange={(v) => setEditFields(prev => ({ ...prev, artist: v }))}
            onArtistSelect={(a) => setEditFields(prev => ({ ...prev, artist: a.name }))}
            placeholder="Search or enter artist"
            className="h-8 text-sm"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1 block">Genre</Label>
          <Input value={editFields.genre} onChange={e => setEditFields(prev => ({ ...prev, genre: e.target.value }))} className="h-8 text-sm" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Type</Label>
            <Select value={editFields.type} onValueChange={v => setEditFields(prev => ({ ...prev, type: v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>
                {['Single','Album','EP','Beat','DJ Mix','Podcast','Mixtape','Live Album','Compilation'].map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
            <Select value={editFields.status} onValueChange={v => setEditFields(prev => ({ ...prev, status: v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Published">Published</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Downloads section */}
        <div className="rounded-lg border border-border/50 p-3 space-y-2.5 bg-muted/20">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Downloads &amp; Licensing</p>
          <div className="flex gap-5">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={editFields.allowDownloads}
                onChange={e => setEditFields(prev => ({ ...prev, allowDownloads: e.target.checked }))} className="rounded" />
              Allow Downloads
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" checked={editFields.explicit}
                onChange={e => setEditFields(prev => ({ ...prev, explicit: e.target.checked }))} className="rounded" />
              Explicit
            </label>
          </div>
          {editFields.allowDownloads && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Download Type</Label>
                <Select value={editFields.downloadType} onValueChange={v => setEditFields(prev => ({ ...prev, downloadType: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pwyw">Name Your Price</SelectItem>
                    <SelectItem value="paid">Fixed Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editFields.downloadType !== 'free' && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    {editFields.downloadType === 'pwyw' ? 'Min Price (¢)' : 'Price (¢)'}
                  </Label>
                  <Input type="number" value={editFields.downloadMinPrice}
                    onChange={e => setEditFields(prev => ({ ...prev, downloadMinPrice: Number(e.target.value) }))}
                    className="h-8 text-sm" placeholder="0" min={0} />
                </div>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Hi-Res File URL (for purchase &amp; sync)</Label>
            <Input value={editFields.hiResUrl} onChange={e => setEditFields(prev => ({ ...prev, hiResUrl: e.target.value }))}
              className="h-8 text-sm" placeholder="Firebase Storage URL of WAV/AIF original" />
            <p className="text-[10px] text-muted-foreground/60 mt-1">Buyers &amp; sync licensees receive this file. Streaming uses the web-optimised version.</p>
          </div>
        </div>

        {/* Lyrics with AI generator */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs text-muted-foreground">Lyrics</Label>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1"
              onClick={handleGenerateLyrics} disabled={lyricsGenerating}>
              {lyricsGenerating ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : '✨'}
              {lyricsGenerating ? 'Generating…' : 'AI Generate'}
            </Button>
          </div>
          <Textarea value={editFields.lyrics} onChange={e => setEditFields(prev => ({ ...prev, lyrics: e.target.value }))}
            className="text-sm min-h-[160px] resize-y" placeholder="Paste lyrics here, or use AI Generate…" />
        </div>
      </div>
    </div>
  ) : null;

  // ─── Full expanded sheet content ─────────────────────────────────────────────

  const fullMediaPlayerSheetContent = (
    <div className="relative h-full w-full bg-background flex flex-col">
      {/* Sticky header */}
      {/* Thumbnail and close are both header-sized squares flush in the
          corners, right under the header's corner buttons (Gordon 2026-11-02). */}
      {/* Frosted like the app header; on phones it floats over the content
          (right under the app header) so the content scrolls under it. */}
      <div className="bb-header-glass flex-shrink-0 sticky top-0 z-10 border-b border-border/50 h-[var(--header-height)] px-0 flex items-center gap-3 max-sm:absolute max-sm:inset-x-0 max-sm:top-[var(--header-height)]">
        {trackImages.length > 0 ? (
          <div className="relative flex-shrink-0 size-[var(--header-height)] self-stretch overflow-hidden bg-muted">
            <SlideshowImage images={trackImages} alt={trackTitle} interval={4000} sizes="64px" />
          </div>
        ) : (
          <div className="size-[var(--header-height)] self-stretch bg-muted flex items-center justify-center flex-shrink-0">
            {isYouTube ? <Video className="h-5 w-5 text-muted-foreground" /> : <Music2 className="h-5 w-5 text-muted-foreground" />}
          </div>
        )}
        <div className="min-w-0 flex-grow">
          <p className="capitalize text-sm font-semibold truncate">{trackTitle}</p>
          {artistHref && trackArtist ? (
            <Link href={artistHref} onClick={() => setIsSheetOpen(false)} className="text-xs text-muted-foreground truncate block hover:text-primary transition-colors hover:underline">{trackArtistDisplay}</Link>
          ) : (
            <p className="text-xs text-muted-foreground truncate">{trackArtistDisplay}</p>
          )}
        </div>
        {/* Close: a corner button like the header's, flush in the window's top
            right corner right under the header corner (Gordon 2026-10-31). */}
        <Button variant="ghost" className="bb-edge-btn flex-shrink-0 size-[var(--header-height)] p-0 self-stretch"
          onClick={() => setIsSheetOpen(false)} aria-label={common('close')} title={common('close')}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* ── Content: single col mobile, two col desktop ── */}
      <div className="flex-1 overflow-y-auto md:overflow-hidden max-sm:pt-[calc(var(--header-height)_*_2)]">
        {/* Three-column on desktop: left artwork | right info | VU strip */}
        <div className="flex flex-col md:flex-row md:h-full">

          {/* ── LEFT PANEL: artwork + controls + related thumbnails ── */}
          <div className="md:w-1/2 md:overflow-y-auto md:border-r md:border-border/40 px-page py-4 md:py-6 md:pb-[calc(var(--player-height)_+_1.5rem)] flex flex-col gap-4">

            {/* Artwork or YouTube embed — wrapper aspect tracks the actual
                video aspect (ytAspect). Landscape is capped at 16:9 so the embed
                is never flatter/shorter than HD ("at least HD"); portrait /
                Shorts may go taller, down to 9:16, for the height they need. */}
            {isYouTube ? (
              <div className="flex flex-col gap-2">
                <div
                  ref={videoFsRef}
                  data-bb-video-fs=""
                  data-bb-aspect={ytAspect}
                  className={cn("relative w-full bg-black", !pseudoFs && "shadow-lg rounded-lg overflow-hidden")}
                  style={pseudoFsStyle ?? { aspectRatio: `${Math.max(9 / 16, Math.min(ytAspect, 16 / 9))}` }}
                >
                  <iframe
                    data-bb-player-yt=""
                    data-bb-muted={ytSettings.muted ? 'true' : 'false'}
                    className="absolute inset-0 w-full h-full"
                    src={getEmbedUrl(currentTrack?.youtubeId || '', {
                      autoplay: ytSettings.autoplay, mute: ytSettings.muted,
                      // No YouTube controls — its middle play/pause/previous
                      // buttons sat on top of the film (Gordon 2026-10-30).
                      loop: ytSettings.loop, controls: false,
                      modestBranding: ytSettings.modestBranding,
                    })}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen title={trackTitle}
                  />
                  {/* Full screen, turned on its side for a landscape video
                      (YouTube's own controls are off here, so this is the way in). */}
                  <Button
                    type="button" variant="ghost" size="icon"
                    onClick={toggleVideoFullscreen}
                    aria-label={videoFs ? tp('exitFullscreen') : tp('fullscreen')}
                    title={videoFs ? tp('exitFullscreen') : tp('fullscreen')}
                    className="absolute bottom-2 right-2 z-10 h-9 w-9 bg-black/60 text-white hover:bg-black/80 hover:text-white"
                  >
                    {videoFs ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </Button>
                </div>
                {/* Title + clickable artist link under the video */}
                <div className="min-w-0">
                  <p className="capitalize text-sm font-semibold truncate">{trackTitle}</p>
                  {trackArtistDisplay && (artistHref
                    ? <Link href={artistHref} className="block truncate text-xs text-muted-foreground hover:text-foreground hover:underline">{trackArtistDisplay}</Link>
                    : <p className="truncate text-xs text-muted-foreground">{trackArtistDisplay}</p>)}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {/* Large artwork — fills left panel width (waveform lives in the right info panel) */}
                <div className="relative w-full aspect-square rounded-xl overflow-hidden shadow-2xl bg-muted">
                  {trackImages.length > 0 ? (
                    <SlideshowImage images={trackImages} alt={trackTitle} interval={5000} priority interactive />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                      <Music2 className="h-16 w-16 text-primary/30" />
                    </div>
                  )}
                  {/* AI mark — top-left, like every cover. */}
                  <CoverAiSticker level={trackAi?.level} className="z-20" />
                  {/* Edit — top-right, for the track's owner and admins (Gordon
                      2026-10-31): opens the track page with its edit window up. */}
                  {canEdit && currentTrack?.id && !(currentTrack as any)?.youtubeId && (
                    <Link
                      href={`/music/${currentTrack.id}?edit=1`}
                      onClick={() => setIsSheetOpen(false)}
                      aria-label={common('edit')}
                      title={common('edit')}
                      className="absolute top-2 right-2 z-20 flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur hover:bg-black/85"
                    >
                      <Pencil className="h-3.5 w-3.5" /> {common('edit')}
                    </Link>
                  )}
                  {/* Audio-reactive FX layer — animates the cover in time with the
                      playing track. Default is the track's own setting; a
                      listener's personal Artwork FX choice overrides it. */}
                  {(() => {
                    const eff = resolveFx(fxPref, {
                      effect: (currentTrack as any)?.artworkFx || 'off',
                      intensity: (currentTrack as any)?.artworkFxIntensity ?? 1,
                      color: (currentTrack as any)?.artworkFxColor || '',
                    });
                    return eff.effects && eff.effects.length > 0 ? (
                      <AudioReactiveArtwork
                        effects={eff.effects}
                        intensity={eff.intensity}
                        color={eff.color || undefined}
                        imageUrl={trackImages[0]}
                      />
                    ) : null;
                  })()}
                  {/* Artwork FX control — every listener can turn it on / pick an effect. */}
                  <ArtworkFxControl
                    trackId={currentTrack?.id}
                    itemDefault={{
                      effect: (currentTrack as any)?.artworkFx || 'off',
                      intensity: (currentTrack as any)?.artworkFxIntensity ?? 1,
                      color: (currentTrack as any)?.artworkFxColor || '',
                    }}
                    className="absolute bottom-2 right-2 z-[20]"
                    openDir="up"
                  />
                  {/* Standard Parental Advisory label on explicit covers (bottom-left, like album art) */}
                  {trackExplicit && (
                    <div className="absolute bottom-2 left-2 w-[22%] max-w-[84px] min-w-[44px]">
                      <ParentalAdvisoryLabel width={84} className="w-full h-auto drop-shadow" />
                    </div>
                  )}
                </div>
                {/* Title + artist + share + play button */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="capitalize text-sm font-semibold truncate" style={(currentTrack as any)?.artworkTitleColor ? { color: (currentTrack as any).artworkTitleColor } : undefined}>{trackTitle}</p>
                    {trackArtistDisplay && (artistHref
                      ? <Link href={artistHref} onClick={e => e.stopPropagation()} className="block truncate text-xs text-muted-foreground hover:text-foreground hover:underline">{trackArtistDisplay}</Link>
                      : <p className="truncate text-xs text-muted-foreground">{trackArtistDisplay}</p>)}
                  </div>
                  {/* Share — one panel with: send to a user in chat (DM),
                      external links (WhatsApp/SMS/email/X), copy link + QR code. */}
                  {currentTrack?.id && (
                    <div onClick={e => e.stopPropagation()}>
                      <SharePanel
                        title={trackArtist ? `${trackArtist} — ${trackTitle}` : trackTitle}
                        subtitle={trackArtist || undefined}
                        url={currentTrack.id ? `/music/${currentTrack.id}` : ''}
                        imageUrl={trackImage}
                        contentType="track"
                        contentId={currentTrack.id}
                        variant="ghost"
                        size="icon"
                        iconOnly
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      />
                    </div>
                  )}
                  <Button variant="default" size="icon"
                    className="rounded-full bg-black border-2 border-white/50 hover:border-white hover:bg-black/80 h-10 w-10 shadow-lg flex-shrink-0"
                    onClick={togglePlay}>
                    {isPlaying ? <Pause className="h-6 w-6 text-[#39FF14] fill-[#39FF14]" /> : <Play className="h-6 w-6 text-[#39FF14] fill-[#39FF14]" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Progress + controls shown in left panel on desktop */}
            {!isYouTube && (
              <div className="hidden md:flex flex-col gap-2">
                {commonProgressSlider()}
                <div className="flex items-center justify-center gap-1">{commonPlaybackControls()}</div>
              </div>
            )}

            {/* YouTube description (desktop: left panel; mobile: below tabs) */}
            {isYouTube && currentTrack?.description && (
              <details className="group hidden md:block">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-1">
                  Description
                  <span className="text-xs group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line leading-relaxed max-h-40 overflow-y-auto">
                  {currentTrack.description}
                </p>
              </details>
            )}

            {/* Related / album thumbnails (from queue) — desktop only. This whole
                LEFT PANEL isn't hidden on mobile (panels just stack), so without
                this it duplicated the mobile copy appended below the tabs
                (md:hidden block further down) — same grid rendered twice on one
                screen, "Up Next"/"More from X" appearing both at the top and at
                the end. Mobile now only ever sees the bottom copy. */}
            <div className="hidden md:contents">
              {relatedGrid}
              {relatedByArtistGrid}
            </div>
          </div>

          {/* ── RIGHT PANEL: title + waveform + meters + tabs ── */}
          {/* Bottom padding = the player bar (+ the VU strip on phones) that sits
              over the sheet's bottom, so the queue's last row can scroll into
              view (Andrés 2026-11-01: track 12 was hidden behind the footer). */}
          <div className="md:w-1/2 md:overflow-y-auto px-page pt-4 md:pt-6 pb-[calc(var(--player-height)_+_2.5rem)] md:pb-[calc(var(--player-height)_+_1.5rem)] flex flex-col gap-4">

            {/* Track metadata + actions — sits above the waveform. On mobile we
                STACK (title full-width on top, action icons below) so the title
                is never squeezed to nothing by the icon row; side-by-side at md+. */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
              <div className="flex-grow min-w-0">
                {/* Title links to the track page; artist to the artist page;
                    album to the release page — all clickable in the player
                    window (Gordon). */}
                {currentTrack?.id ? (
                  <Link href={`/music/${currentTrack.id}`} onClick={() => setIsSheetOpen(false)} className="block">
                    <h3 className="capitalize text-2xl font-bold truncate hover:text-primary transition-colors">{trackTitle}</h3>
                  </Link>
                ) : (
                  <h3 className="capitalize text-2xl font-bold truncate">{trackTitle}</h3>
                )}
                {trackArtist && (
                  <p className="text-md text-muted-foreground mt-0.5">
                    {artistHref ? (
                      <Link href={artistHref} onClick={() => setIsSheetOpen(false)} className="hover:text-primary hover:underline transition-colors">{trackArtistDisplay}</Link>
                    ) : trackArtistDisplay}
                  </p>
                )}
                {trackAlbum && (() => {
                  const albumReleaseId = (currentTrack as any)?.releaseId || resolvedReleaseId;
                  return albumReleaseId ? (
                    <Link href={`/music/${albumReleaseId}`} onClick={() => setIsSheetOpen(false)} className="text-sm text-muted-foreground/70 hover:text-primary hover:underline transition-colors">{trackAlbum}</Link>
                  ) : (
                    <p className="text-sm text-muted-foreground/70">{trackAlbum}</p>
                  );
                })()}
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {/* Explicit "E" mark (standard) + how-it-was-made provenance */}
                  {trackExplicit && <ExplicitBadge explicit size={16} />}
                  {trackAi?.level && <AiAuthorshipBadge level={trackAi.level} percent={trackAi.percent} />}
                  {(currentTrack as any)?.fileFormat && (
                    <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {(currentTrack as any).fileFormat}
                    </span>
                  )}
                  {duration > 0 && (
                    <span className="text-[10px] text-muted-foreground">{formatTime(duration)}</span>
                  )}
                  <span className="text-[10px] font-mono uppercase text-muted-foreground/50">
                    {streamQuality === 'lossless' ? 'WAV' : streamQuality === 'streaming' ? 'M4A' : 'AUTO'}
                  </span>
                  {/* BPM + musical key — analysed once, free, client-side. */}
                  {!isYouTube && trackAnalysis && trackAnalysis.trackId === currentTrack?.id && (
                    trackAnalysis.pending ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />Analysing…
                      </span>
                    ) : (trackAnalysis.bpm || trackAnalysis.key) ? (
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/15 text-primary" title="Detected tempo and musical key">
                        {trackAnalysis.bpm > 0 && <span>{trackAnalysis.bpm} BPM</span>}
                        {trackAnalysis.bpm > 0 && trackAnalysis.key && <span className="opacity-40">·</span>}
                        {trackAnalysis.key && <span>{trackAnalysis.key}</span>}
                      </span>
                    ) : null
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* Claim Credits — left of favorites in the track header so it's
                    immediately discoverable while listening. */}
                {currentTrack && (
                  <ClaimCreditsButton
                    mediaId={currentTrack.id}
                    mediaType="work"
                    mediaTitle={currentTrack.title || "Untitled"}
                    iconOnly
                    className={SHEET_ACTION_BTN}
                  />
                )}
                <Button variant="ghost" size="icon" className={SHEET_ACTION_BTN}
                  aria-label={common(isFavoritedState ? "removeFromFavorites" : "addToFavorites")} aria-pressed={isFavoritedState}
                  onClick={handleToggleFavorite}>
                  <Heart className={cn("h-5 w-5", isFavoritedState && "fill-primary text-primary")} />
                </Button>
                {currentTrack && (
                  <div onClick={e => e.stopPropagation()}>
                    <AddToPlaylist
                      track={{
                        trackId: currentTrack.id,
                        title: currentTrack.title,
                        artist: currentTrack.artist || '',
                        imageUrl: currentTrack.imageUrl,
                        audioUrl: currentTrack.audioUrl,
                        youtubeId: currentTrack.youtubeId,
                        duration: currentTrack.duration,
                      }}
                      className={SHEET_ACTION_BTN}
                    />
                  </div>
                )}
                {currentTrack && !isLinkedVideo(currentTrack) && (
                  <DownloadButton
                    item={{
                      id: currentTrack.id,
                      title: currentTrack.title,
                      artist: currentTrack.artist,
                      coverArtUrl: currentTrack.imageUrl,
                      allowDownloads: editFields.allowDownloads,
                      downloadType: editFields.downloadType as "free" | "pwyw" | "paid",
                      downloadMinPrice: editFields.downloadMinPrice,
                      hiResUrl: editFields.hiResUrl,
                      audioUrl: currentTrack.audioUrl,
                    }}
                    compact
                    className={SHEET_ACTION_BTN}
                  />
                )}
                {/* Share — right next to Download in the player header. */}
                {currentTrack && (
                  <ShareButton
                    title={currentTrack.artist ? `${currentTrack.artist} — ${currentTrack.title}` : currentTrack.title}
                    url={`/music/${currentTrack.id}`}
                    variant="outline"
                    size="icon"
                    iconOnly
                    className={SHEET_ACTION_BTN}
                  />
                )}
              </div>
            </div>

            {/* Waveform + meters: one section with its own header — Expand and
                Show/Hide as icon buttons, top right (Gordon 2026-11-02). */}
            {isActive && (
              <section id="player-inline-meters" aria-label={tp('waveMeters')} className="flex-shrink-0 space-y-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{tp('waveMeters')}</h3>
                  <div className="ml-auto flex items-center gap-1.5">
                    {metersShown && (
                      <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                        aria-expanded={metersExpanded} aria-label={common(metersExpanded ? 'collapse' : 'expand')} title={common(metersExpanded ? 'collapse' : 'expand')}
                        onClick={() => setMetersExpanded(expanded => !expanded)}>
                        {metersExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                      </Button>
                    )}
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8"
                      aria-pressed={metersShown} aria-label={tp(metersShown ? 'hideMeters' : 'showMeters')} title={tp(metersShown ? 'hideMeters' : 'showMeters')}
                      onClick={toggleMeters}>
                      {metersShown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                {metersShown && (
                  <>
                    {/* Waveform — audio tracks only */}
                    {!isYouTube && currentTrack?.audioUrl && (
                      <HelpBubble
                        title="Waveform timeline"
                        description="Visual map of the track's audio. Click anywhere on the wave to seek. Orange pins are timestamped fan comments — click a pin to jump to that moment. The style picker (bars / curve / dots) and VU toggle are in the top-right of the waveform."
                        learnMoreAnchor="media-player"
                        aiQuestion="How do I add a timestamped comment to a Breakin' Bread track?"
                        side="top"
                        block
                        className="rounded-lg overflow-hidden flex-shrink-0"
                      >
                        <TrackWaveform
                          audioUrl={currentTrack.audioUrl}
                          durationSeconds={currentTrack.duration}
                          comments={trackComments.filter(c => !c.isGlobal && c.timestampSeconds != null)}
                          height={101}
                          showVu={false}
                          className="rounded-lg overflow-hidden flex-shrink-0"
                        />
                      </HelpBubble>
                    )}
                    <InlineAudioMeters height={metersExpanded ? 350 : 175} />
                  </>
                )}
              </section>
            )}

            {/* Mobile only: progress + controls (above tabs) */}
            {!isYouTube && (
              <div className="flex md:hidden flex-col gap-2">
                {commonProgressSlider()}
                <div className="flex items-center justify-center gap-1">{commonPlaybackControls()}</div>
              </div>
            )}

            {/* Mobile: YouTube description */}
            {isYouTube && currentTrack?.description && (
              <details className="group md:hidden">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground flex items-center gap-1">
                  Description
                  <span className="text-xs group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line leading-relaxed max-h-40 overflow-y-auto">
                  {currentTrack.description}
                </p>
              </details>
            )}

            {/* Tabs: Queue | Lyrics | Credits | Comments | Web Info | Edit (admins/owners only) */}
            <Tabs defaultValue="queue" className="w-full flex flex-col">
              <TabsList className={cn("grid w-full flex-shrink-0 h-auto", canEdit ? "grid-cols-6" : "grid-cols-5")}>
                {/* Mobile: icons only — long labels made the tab row explode
                    on phones. Labels return from sm up. */}
                <TabsTrigger value="queue" className="h-9 text-xs gap-1" aria-label={tp('queue')}>
                  <ListMusic className="h-3.5 w-3.5 sm:h-3 sm:w-3" /><span className="hidden sm:inline">{tp('queue')}</span>
                </TabsTrigger>
                <TabsTrigger value="lyrics" className="h-9 text-xs gap-1" aria-label={isYouTube ? tp('info') : tp('lyrics')}>
                  <FileText className="h-3.5 w-3.5 sm:h-3 sm:w-3" /><span className="hidden sm:inline">{isYouTube ? tp('info') : tp('lyrics')}</span>
                </TabsTrigger>
                <TabsTrigger value="credits" className="h-9 text-xs gap-1" aria-label={tp('credits')}>
                  <Users className="h-3.5 w-3.5 sm:h-3 sm:w-3" /><span className="hidden sm:inline">{tp('credits')}</span>
                </TabsTrigger>
                <TabsTrigger value="comments" className="h-9 text-xs">
                  <span className="inline-flex items-center gap-1 leading-none">
                    <MessageCircle className="h-3 w-3" />
                    {trackComments.length > 0 && <span className="text-[10px]">{trackComments.length}</span>}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="web" className="h-9 gap-1" aria-label={tp('web')}>
                  <Globe className="h-3.5 w-3.5 sm:h-3 sm:w-3" /><span className="hidden sm:inline">{tp('web')}</span>
                </TabsTrigger>
                {canEdit && (
                  <TabsTrigger value="edit" title="Edit track details" className="h-9">
                    <Pencil className="h-3.5 w-3.5" />
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Queue */}
              <TabsContent value="queue" className="mt-0">
                {queueTabContent}
              </TabsContent>

              {/* Lyrics / Description */}
              <TabsContent value="lyrics" className="mt-4">
                <div className="min-h-[160px] max-h-[55vh] overflow-y-auto pb-24 pr-1">
                  {isYouTube ? (
                    currentTrack?.description ? (
                      <p className="text-sm leading-relaxed whitespace-pre-line">{currentTrack.description}</p>
                    ) : (
                      <div className="text-center py-12 space-y-3">
                        <Video className="h-12 w-12 mx-auto text-muted-foreground/30" />
                        <p className="text-sm text-muted-foreground">No description available</p>
                      </div>
                    )
                  ) : (currentTrack?.lyrics || editFields.lyrics) ? (
                    <div className="space-y-4">
                      {/* Translation toolbar */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <Languages className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <Select value={playerLang} onValueChange={setPlayerLang}>
                          <SelectTrigger className="h-7 text-xs w-[130px] flex-shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PLAYER_LANGUAGES.map(l => (
                              <SelectItem key={l.code} value={l.code} className="text-xs">{l.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs px-2"
                          onClick={handlePlayerTranslate}
                          disabled={playerTranslating}
                        >
                          {playerTranslating
                            ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Translating…</>
                            : <><Languages className="h-3 w-3 mr-1" />Translate</>
                          }
                        </Button>
                        {playerTranslations[playerLang] && (
                          <Button
                            size="sm"
                            variant={showPlayerTranslation ? 'default' : 'ghost'}
                            className="h-7 text-xs px-2"
                            onClick={() => setShowPlayerTranslation(v => !v)}
                          >
                            {showPlayerTranslation ? 'Original' : 'Translated'}
                          </Button>
                        )}
                        {lyricsSaved && !lyricsEditing && (
                          <span className="text-xs text-green-500 flex items-center gap-1 ml-auto"><Check className="h-3 w-3" />Saved</span>
                        )}
                        {/* Edit the transcript — owner, admin, any creator, or a
                            credited contributor on THIS track (Gordon: contributors
                            can edit the text). Saves to the work for everyone. */}
                        {canEditLyrics && !lyricsEditing && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className={cn("h-7 text-xs px-2", !lyricsSaved && "ml-auto")}
                            onClick={() => {
                              setShowPlayerTranslation(false);
                              setEditFields(prev => ({ ...prev, lyrics: (currentTrack?.lyrics || prev.lyrics || '') }));
                              setLyricsEditing(true);
                            }}
                            title="Edit the transcribed lyrics"
                          >
                            <Pencil className="h-3 w-3 mr-1" />Edit
                          </Button>
                        )}
                      </div>
                      {/* Auto-transcribed, not yet verified by a human. */}
                      {lyricsMeta.auto && !lyricsMeta.verified && !lyricsEditing && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/15 px-2.5 py-1.5">
                          <Sparkles className="h-3.5 w-3.5 text-amber-300 mt-0.5 flex-shrink-0" />
                          <p className="text-[11px] text-amber-100 leading-snug">
                            <span className="font-semibold text-amber-300">Auto-transcribed</span> — AI wrote these from the audio and no one has verified them yet.
                            {canEditLyrics ? ' Spot a mistake? Tap Edit to fix it.' : ' Sign in to help correct them.'}
                          </p>
                        </div>
                      )}
                      {/* Lyrics content — read-only, or an inline editor for editors */}
                      {lyricsEditing ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editFields.lyrics}
                            onChange={e => setEditFields(prev => ({ ...prev, lyrics: e.target.value }))}
                            className="min-h-[220px] text-sm leading-relaxed"
                            placeholder="Type or correct the lyrics / transcript…"
                          />
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button size="sm" onClick={handleSaveLyrics} disabled={lyricsSaving} className="gap-1">
                              {lyricsSaving
                                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</>
                                : <><Save className="h-3.5 w-3.5" />Save lyrics</>}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setLyricsEditing(false)} disabled={lyricsSaving}>
                              Cancel
                            </Button>
                            <span className="text-[11px] text-muted-foreground ml-auto">Saved to the track for everyone.</span>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm leading-relaxed whitespace-pre-line">
                          {showPlayerTranslation && playerTranslations[playerLang]
                            ? playerTranslations[playerLang]
                            : (currentTrack?.lyrics || editFields.lyrics)
                          }
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 space-y-3">
                      <Music2 className="h-12 w-12 mx-auto text-muted-foreground/30" />
                      <p className="text-sm text-muted-foreground">No lyrics available</p>
                      {canEditLyrics && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handlePlayerGenerateLyrics}
                          disabled={playerLyricsGenerating}
                          className="gap-1"
                          title="Listens to the recording and writes down the REAL lyrics sung on the track."
                        >
                          {playerLyricsGenerating
                            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Transcribing…</>
                            : <><Sparkles className="h-3.5 w-3.5" />Transcribe from Audio</>
                          }
                        </Button>
                      )}
                      {!canEditLyrics && <p className="text-xs text-muted-foreground/60">Add lyrics via the admin panel or upload form</p>}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Credits */}
              <TabsContent value="credits" className="mt-2 flex-1 overflow-y-auto">
                <div className="min-h-[200px] flex flex-col">
                  {resolvedCredits.length > 0 ? (
                    <div className="space-y-3 text-sm">
                      {resolvedCredits.map((credit, i) => (
                        <CreditRow key={i} name={credit?.name || (credit as any)?.displayName || 'Unknown'}
                          role={credit?.role || (credit as any)?.type || 'Contributor'} href={credit?.href}
                          avatarUrl={credit?.avatarUrl} onOpen={() => setIsSheetOpen(false)} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 space-y-3">
                      <Music2 className="h-12 w-12 mx-auto text-muted-foreground/30" />
                      <p className="text-sm text-muted-foreground">No credits available yet</p>
                      <p className="text-xs text-muted-foreground/60">If you worked on this track, claim your credit below.</p>
                    </div>
                  )}

                  {/* Claim Credits CTA — always present on this tab so anyone who
                      worked on the track can submit a credit claim for admin review.
                      This is BB's transparent fair-credit ethos baked into the player. */}
                  {currentTrack && (
                    <div className="mt-auto pt-4 border-t border-border/50 space-y-2">
                      {/* Owner / admin: jump to the track page to add participants
                          + set splits (the full ParticipantPanel lives there).
                          Gordon: "be able to add participants and credits as
                          super admin or content creator on one's own work." */}
                      {canEdit && (
                        <Link href={`/music/${currentTrack.id}`} onClick={() => setIsSheetOpen(false)} className="block">
                          <Button variant="default" className="w-full gap-1.5">
                            <UserPlus className="h-4 w-4" /> Manage credits &amp; participants
                          </Button>
                        </Link>
                      )}
                      <p className="text-[11px] text-muted-foreground mb-2">
                        Worked on <span className="font-medium text-foreground">{currentTrack.title}</span>? Submit your credit — admins review every claim.
                      </p>
                      <ClaimCreditsButton
                        mediaId={currentTrack.id}
                        mediaType="work"
                        mediaTitle={currentTrack.title || "Untitled"}
                        className="w-full"
                      />
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Comments */}
              <TabsContent value="comments" className="mt-2 flex-1 overflow-y-auto">
                <div className="space-y-4">
                  {/* Toggle timeline comments */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowTimelineComments(v => !v)}
                      className={cn(
                        "text-xs px-2.5 py-1 rounded-full border transition-colors",
                        showTimelineComments
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-muted-foreground hover:text-foreground"
                      )}
                    >
                      Timeline
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTimelineComments(v => !v)}
                      className={cn(
                        "text-xs px-2.5 py-1 rounded-full border transition-colors",
                        !showTimelineComments
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-muted-foreground hover:text-foreground"
                      )}
                    >
                      All
                    </button>
                    <span className="text-[10px] text-muted-foreground ml-auto">{trackComments.length} comment{trackComments.length !== 1 ? 's' : ''}</span>
                  </div>

                  {/* Comment input */}
                  {user && (
                    <div className="flex items-start gap-2">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        {user.photoURL && <AvatarImage src={user.photoURL} />}
                        <AvatarFallback name={user.displayName || 'U'}>{getInitials(user.displayName || 'U')}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-1.5">
                        <Textarea
                          value={newComment}
                          onChange={e => setNewComment(e.target.value)}
                          placeholder="Write a comment..."
                          className="min-h-[60px] text-sm resize-none"
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(); } }}
                        />
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={pinCommentToTime}
                              onChange={e => setPinCommentToTime(e.target.checked)}
                              className="rounded border-border"
                            />
                            Pin to {formatTime(currentTime)}
                          </label>
                          <Button size="sm" className="h-7 px-3 text-xs gap-1" onClick={handleSubmitComment} disabled={!newComment.trim() || isSubmittingComment}>
                            {isSubmittingComment ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                            Post
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Comments list */}
                  <div className="space-y-3">
                    {trackComments
                      .filter(c => showTimelineComments ? !c.isGlobal : true)
                      .sort((a, b) => {
                        if (showTimelineComments) return (a.timestampSeconds || 0) - (b.timestampSeconds || 0);
                        const aTime = a.createdAt?.toDate?.() || new Date(0);
                        const bTime = b.createdAt?.toDate?.() || new Date(0);
                        return bTime.getTime() - aTime.getTime();
                      })
                      .map(comment => (
                        <div key={comment.id} className="flex gap-2 group">
                          <Avatar className="h-7 w-7 flex-shrink-0 mt-0.5">
                            {comment.userImageUrl && <AvatarImage src={comment.userImageUrl} />}
                            <AvatarFallback className="text-[10px]" name={comment.userName || 'U'}>{getInitials(comment.userName || 'U')}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium">{comment.userName}</span>
                              {comment.timestampSeconds != null && (
                                <button
                                  className="text-[10px] font-mono text-primary hover:underline"
                                  onClick={() => seek(comment.timestampSeconds!)}
                                >
                                  {formatTime(comment.timestampSeconds)}
                                </button>
                              )}
                              <span className="text-[10px] text-muted-foreground">
                                {(() => {
                                  const d = comment.createdAt?.toDate?.() || new Date();
                                  const diff = Date.now() - d.getTime();
                                  const mins = Math.floor(diff / 60000);
                                  if (mins < 1) return 'now';
                                  if (mins < 60) return `${mins}m`;
                                  const hrs = Math.floor(mins / 60);
                                  if (hrs < 24) return `${hrs}h`;
                                  return `${Math.floor(hrs / 24)}d`;
                                })()}
                              </span>
                            </div>
                            <p className="text-sm text-foreground/90 mt-0.5">{comment.content}</p>
                          </div>
                          {user?.uid === comment.userId && (
                            <Button
                              variant="ghost" size="icon"
                              className="h-6 w-6 bb-hover-reveal text-muted-foreground hover:text-destructive"
                              onClick={async () => {
                                const trackId = currentTrack?.id?.includes('-track-') ? (currentTrack as any).workId || currentTrack?.id : currentTrack?.id;
                                if (trackId) await deleteTrackComment(trackId, comment.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      ))}
                    {trackComments.length === 0 && (
                      <div className="text-center py-8">
                        <MessageCircle className="h-10 w-10 mx-auto text-muted-foreground/20 mb-2" />
                        <p className="text-sm text-muted-foreground">No comments yet</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">Be the first to comment on this track</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Web Info */}
              <TabsContent value="web" className="mt-2 flex-1 overflow-y-auto">
                {(trackArtist || trackTitle) ? (
                  <WebResultsSection
                    query={trackArtist || trackTitle}
                    entityType="artist"
                    entityName={trackArtist || trackTitle}
                    canManage={false}
                  />
                ) : (
                  <div className="text-center py-12">
                    <p className="text-sm text-muted-foreground">No track loaded</p>
                  </div>
                )}
              </TabsContent>

              {/* Edit (super admin / admin / track owner only) */}
              {canEdit && (
                <TabsContent value="edit" className="mt-0 flex-1 overflow-y-auto">
                  {editTabContent}
                </TabsContent>
              )}
            </Tabs>

            {/* Mobile: related thumbnails appended below tabs */}
            <div className="md:hidden pb-28">
              {relatedGrid}
              {relatedByArtistGrid}
            </div>
          </div>

          {/* VU strip removed — use the global VU strip popup for metering */}
        </div>
      </div>
    </div>
  );

  // ─── Toggle button + player bar classes (unchanged) ──────────────────────────

  // The icon reads on any theme's toggle colour (Gordon 2026-11-01: white X on a white button).
  const [toggleIconColor, setToggleIconColor] = useState('#fff');
  useEffect(() => {
    const pick = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--player-toggle-background').trim();
      const l = parseFloat(v.split(/\s+/)[2] ?? '30');
      setToggleIconColor(Number.isFinite(l) && l > 55 ? '#0a0a0a' : '#fff');
    };
    pick();
    const mo = new MutationObserver(pick);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class', 'data-theme'] });
    return () => mo.disconnect();
  }, []);

  const playerToggleButtonClasses = cn(
    // Hidden on mobile — thumbnail click handles open/close there.
    // On desktop it fills the RIGHT sidebar-icon column (mirrors the chat FAB,
    // which now docks bottom-left — Gordon: "change place on the play and chat
    // button in the footer"), always full player-height.
    "fixed z-[100] shadow-lg items-center justify-center transition-all duration-300 hover:opacity-90 p-0",
    "bottom-0 right-0 rounded-none w-[var(--sidebar-width-icon)] menuright:right-auto menuright:left-0",
    "hidden sm:flex",   // ← mobile: hidden; desktop: shown
    // Always player-height tall on desktop so the footer anchor never shifts.
    "sm:h-[var(--player-height)]",
  );

  const playerBarPositionClasses = cn(
    // Always visible — permanent player bar on all pages.
    // transition-[left,width,height] instead of transition-all — avoids iOS Safari
    // creating a compositing layer that makes the fixed element scroll with the page.
    "fixed z-[100] flex items-center justify-between py-0 text-foreground transition-[left,width,height] duration-300 ease-in-out",
    // Translucent theme colour + blur, set per signed-in person and by Super
    // Admin for visitors (globals.css .bb-player-bar, lib/footer-style.ts);
    // text and icons keep a shadow. No data-bg="player" — themes paint that
    // with !important.
    "bb-player-bar bb-chrome-text-shadow bb-chrome-icon-shadow",
    "bottom-0",
    // Mobile: leave the left 56px for the chat FAB (which sits at bottom-0 left-0)
    "right-0 w-[calc(100%-56px)] h-[56px] menuright:max-sm:right-auto menuright:max-sm:left-0",
    // Desktop: offset for sidebar icon columns on both sides.
    // Always full player-height — keeps the footer anchor constant whether idle or active.
    "sm:left-[var(--sidebar-width-icon)] sm:w-[calc(100%-2*var(--sidebar-width-icon))] sm:h-[var(--player-height)]",
  );

  const sheetContentClasses = cn(
    "fixed z-[45] p-0 border-none outline-none focus:ring-0 bg-card",
    // Phones: the window runs up BEHIND the frosted app header (stacked just
    // under it) so the artwork scrolls under both headers (Gordon 2026-11-02).
    isMobile ? "inset-0 top-0 z-[39]" :
      (displayMode === "full"
        ? "left-[var(--sidebar-width)] right-[var(--sidebar-width-icon)] top-[var(--header-height)] menuright:left-[var(--sidebar-width-icon)] menuright:right-[var(--sidebar-width)]"
        : "left-[var(--sidebar-width-icon)] right-[var(--sidebar-width-icon)] top-[var(--header-height)]"),
    // The VU strip's column (right in the classic layout, left when the menu
    // is on the right) is given to the window while the meter is hidden.
    !isMobile && "[html:not([data-menu-side=right])[data-vu-strip=off]_&]:right-0 [html[data-menu-side=right][data-vu-strip=off]_&]:left-0",
    isMobile ? "h-[100dvh]" : "h-[calc(100vh_-_var(--header-height))]",
    // The turned video frame lives inside this sheet — lift the sheet over
    // the header and player bar while it's open (it's a full-screen view).
    pseudoFs && "!z-[300]"
  );

  // Don't render the player at all until a track has been loaded. But when a
  // track IS loaded and the player was closed (isActive false), keep rendering
  // the toggle button — closing the player used to unmount this whole
  // component INCLUDING the reopen button, leaving music playing with no way
  // to bring the player back (Gordon: "media player button should show when a
  // track is playing at all times").
  if (!hasMounted || (!isActive && !currentTrack)) return null;

  return (
    <>
      {/* Explicit content age gate — one-time confirmation, then remembered. */}
      {explicitGate && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/75 p-4">
          <div className="max-w-sm w-full rounded-2xl border border-border bg-background p-5 space-y-3 text-center shadow-2xl">
            <ParentalAdvisoryLabel width={96} className="mx-auto" />
            <h3 className="text-lg font-bold">Explicit content</h3>
            <p className="text-sm text-muted-foreground">
              This track is flagged <strong>Explicit</strong> (parental advisory — strong language or mature themes). Confirm you&apos;re 18 or older to continue.
            </p>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setExplicitGate(false)}>Not now</Button>
              <Button className="flex-1" onClick={() => { try { localStorage.setItem('bb-explicit-ok', '1'); } catch { /* ignore */ } try { window.dispatchEvent(new CustomEvent('bb:explicit-ok')); } catch { /* ignore */ } setExplicitGate(false); if (!isPlaying) togglePlay(); }}>
                I&apos;m 18+ — continue
              </Button>
            </div>
          </div>
        </div>
      )}
      <Button
        className={playerToggleButtonClasses}
        {...swap}
        style={{ ...swap.style, backgroundColor: "hsl(var(--player-toggle-background))", color: toggleIconColor }}
        onClick={handleToggleButtonClick}
        aria-label={isSheetOpen ? "Close media window" : isActive ? "Close media player" : "Open media player"}
      >
        {isSheetOpen ? <X className="h-8 w-8" /> : isActive ? <X className="h-8 w-8" /> : <Play className="h-6 w-6 fill-current" />}
      </Button>

      {/* translate3d(0,0,0) promotes this fixed element to its own GPU compositing layer on iOS,
          ensuring position:fixed stays anchored to the viewport and doesn't scroll with the page */}
      {/* Bar renders only while the player is open — after an explicit close
          (isActive false, track still loaded) only the toggle button stays. */}
      {isActive && <div className={playerBarPositionClasses}
        style={{ WebkitTransform: 'translate3d(0,0,0)', transform: 'translate3d(0,0,0)' }}
      >
        {isActive ? (
          isMobile ? (
            // On mobile the toggle button is hidden, so no left offset needed.
            // pr-[56px] reserves space for the chat FAB on the right.
            <div className="w-full">
              {compactMobilePlayerBar}
            </div>
          ) : (
            desktopPlayerControls
          )
        ) : (
          // ── Idle state — permanent player bar, no track loaded ────────────
          isMobile ? (
            <div className="w-full flex items-center gap-3 pl-[64px] pr-4 h-[56px]">
              <div className="flex-shrink-0 w-10 h-10 rounded bg-muted flex items-center justify-center">
                <Music2 className="h-5 w-5 text-muted-foreground/50" />
              </div>
              <p className="text-sm text-muted-foreground/70 truncate">
                Click <Play className="inline h-3.5 w-3.5 mb-0.5 text-muted-foreground/70" /> on any track to start playing
              </p>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center gap-3 text-muted-foreground/60 h-full">
              <Music2 className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">Click <Play className="inline h-3.5 w-3.5 mb-0.5" /> on any track to start playing</span>
            </div>
          )
        )}
      </div>}

      <Sheet open={isSheetOpen && isActive} onOpenChange={setIsSheetOpen} modal={false}>
        <SheetContent side="bottom" className={sheetContentClasses} showCloseButton={false} showOverlay={false}
          // A tap on the bar's cover/title toggles the sheet itself — don't let the
          // outside-tap close fire first and the toggle reopen it (Gordon 2026-11-01).
          onInteractOutside={(e) => { if ((e.target as Element | null)?.closest?.('[data-player-sheet-toggle]')) e.preventDefault(); }}
          // Opening focused the close button, whose focus ring drew a bigger
          // frame round it (Gordon 2026-11-02) — focus the window itself.
          onOpenAutoFocus={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement | null)?.focus?.({ preventScroll: true }); }}>
          {fullMediaPlayerSheetContent}
        </SheetContent>
      </Sheet>

      {/* Super admin hide/erase confirm — "xtra security check" before either
          action fires, since both are reachable right from the queue row. */}
      <Dialog open={!!queueActionConfirm} onOpenChange={(open) => !open && !queueActionBusy && setQueueActionConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", queueActionConfirm?.action === "erase" && "text-destructive")}>
              <AlertCircle className="h-5 w-5" />
              {queueActionConfirm?.action === "erase" ? "Erase track"
                : queueActionConfirm?.action === "hide" ? "Hide track"
                : "Unhide track"}
            </DialogTitle>
            <DialogDescription>
              {queueActionConfirm?.action === "erase"
                ? <>Permanently delete &ldquo;{queueActionConfirm?.title}&rdquo;? This removes the track entirely and cannot be undone.</>
                : queueActionConfirm?.action === "hide"
                  ? <>Hide &ldquo;{queueActionConfirm?.title}&rdquo; from public listings? It stays in the catalog for admins and can be unhidden any time.</>
                  : <>Make &ldquo;{queueActionConfirm?.title}&rdquo; visible in public listings again?</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQueueActionConfirm(null)} disabled={queueActionBusy}>Cancel</Button>
            <Button
              variant={queueActionConfirm?.action === "erase" ? "destructive" : "default"}
              onClick={runQueueAction}
              disabled={queueActionBusy}
            >
              {queueActionBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : queueActionConfirm?.action === "erase" ? <Trash2 className="h-4 w-4 mr-1" /> : queueActionConfirm?.action === "hide" ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
              {queueActionConfirm?.action === "erase" ? "Erase" : queueActionConfirm?.action === "hide" ? "Hide" : "Unhide"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** One credit in the player window — the whole row opens the person's page
 *  (Gordon 2026-11-02: "still can't click on the contributors"). Rows that
 *  arrive without a link (credits carried on the track) look the name up. */
function CreditRow({ name, role, href, avatarUrl, onOpen }: { name: string; role: string; href?: string; avatarUrl?: string; onOpen: () => void }) {
  const [link, setLink] = useState(href || '');
  useEffect(() => {
    if (href) { setLink(href); return; }
    let alive = true;
    // No page yet → their credits in search, so every name opens something.
    void creditHref(name).then(h => { if (alive) setLink(h || `/search?q=${encodeURIComponent(name)}`); });
    return () => { alive = false; };
  }, [name, href]);
  const body = (
    <>
      <Avatar className="h-10 w-10">
        {avatarUrl && <AvatarImage src={avatarUrl} />}
        <AvatarFallback name={name}>{getInitials(name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className={cn("font-medium truncate", link && "group-hover:underline")}>{name}</p>
        <p className="text-xs text-muted-foreground">{role}</p>
      </div>
      {link && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
    </>
  );
  return link ? (
    <Link href={link} onClick={onOpen} className="group flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/30 transition-colors">{body}</Link>
  ) : (
    <div className="flex items-center gap-3 p-2 rounded-lg">{body}</div>
  );
}
